/* eslint-disable no-console */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Personal, UpdatePersonalData } from '../../types';
import { useUpdatePersonal } from '../../hooks/usePersonal';
import { useAsignaciones } from '../../hooks/useAsignaciones';
import { useUpdatePersonalData, useUpdateNombre } from '../../hooks/useNombres';
import { useQueryClient } from '@tanstack/react-query';
import apiService, { PrereqArea } from '../../services/api';
import { useAreaCatalog, AREA_BADGE_CLASSES } from '../../hooks/useAreaCatalog';
import { useEstados } from '../../hooks/useEstados';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { X, Trash2, Edit, FileText, Plus, User, Upload, Download, Save, XCircle, Activity, MapPin, Car, Shirt as ShirtIcon, GraduationCap, Copy, Shield, ChevronDown, ChevronUp, Search, Eye, Link2, Check, ArrowRightCircle, History, Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import { useDocumentosByPersona, useDeleteDocumentoAndDrive, useDownloadDocumento, useUpdateDocumento, useDeleteDocumentoWithClones } from '../../hooks/useDocumentos';
import { useCursosByRut, useDeleteCurso } from '../../hooks/useCursos';
import { displayValue } from '../../utils/display';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { getAge, formatRUT, standardizeName, formatDate, truncateFilename, daysUntilNumber, daysUntilText, getDocumentIcon, getDocumentColor, parseDateStringToLocal } from '../../utils/formatters';
import { API_CONFIG } from '../../config/api';
import { log as cloudRunLog } from '../../utils/cloudRunLogger';
import { useProfileImage } from '../../hooks/useProfileImage';
import { mapRequisitosBelray, findBelrayCliente, resolveDocumentoRequisito } from '../../utils/belrayRequisitos';
import { claseEPP, CLASE_EPP_LABEL, ClaseEPP } from '../../utils/epp';
import { descargarVersionesDocumentoPDF } from '../../utils/documentosPdfExporter';
import SubirDocumentoModal from './SubirDocumentoModal';
import { CursoModal } from './CursoModal';
import CourseDocumentModal from './CourseDocumentModal';
import EditDocumentModal from './EditDocumentModal';
import CloneDocumentModal from './CloneDocumentModal';
import DocumentPreviewModal from './DocumentPreviewModal';
import { ChangePersonalDataModal } from './ChangePersonalDataModal';
import CentroCostoMassModal from './CentroCostoMassModal';
import centrosStatic from '../../data/centrosCosto.json';
import { esClienteServicio } from '../../constants/servicioKeywords';
import { useAuthStore } from '../../store/authStore';
import { hasPermission, PERMISSIONS } from '../../utils/authRoles';
import { setEstadoRango, updateEstadoRango, deleteEstadoRango, getEstadosPersona, EstadoRangoEvento } from '../../services/programacionSemanal';
import { useIsMobile } from '../../hooks/useIsMobile';

// Tipos de documento que están restringidos: sólo ciertos roles pueden ver/editar
// Incluir variantes solicitadas: contrato, f30, certificado(s) de cotizaciones, liquidación
// 'certificado' se excluye a propósito: los cursos/certificados deben ser visibles para todos los usuarios.
const RESTRICTED_TIPO_KEYWORDS = ['contrato', 'f30', 'cotizacion', 'cotizaciones', 'liquidacion', 'sueldo'];
// Tipos que son cursos/certificaciones: nunca se restringen (todos los usuarios pueden verlos).
// Debe cubrir los mismos tipos que `courseDocuments`.
const COURSE_TIPOS = new Set([
  'curso', 'certificacion', 'curso/certificacion',
  'certificado_curso', 'diploma', 'certificado_seguridad', 'certificado_vencimiento',
]);
// Tipos gen\u00e9ricos/vac\u00edos: no clasifican el documento, as\u00ed que para ellos s\u00ed se mira el
// nombre para decidir si est\u00e1 restringido.
const TIPOS_GENERICOS = new Set(['', 'otro', 'otros', 'documento', 'general', 'sin_tipo']);
const normalizeForMatch = (s: string) =>
  String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const isCourseDoc = (doc: any): boolean => COURSE_TIPOS.has(normalizeForMatch(doc?.tipo_documento || ''));
// Coincidencia por palabra completa: evita que "irl"/"odi" u otros documentos queden
// restringidos s\u00f3lo porque su nombre libre menciona de pasada una palabra sensible
// (ej. "IRL anexo contrato"), que dejaba la fila sin bot\u00f3n de editar y sin explicaci\u00f3n.
// Colores del badge de EPP en la ventana de versiones. La capacitación va en gris a
// propósito: es el único de los tres que NO acredita una entrega, y pintarla igual que un
// registro es justo la confusión que el badge existe para evitar.
const CLASE_EPP_BADGE: Record<ClaseEPP, string> = {
  registro: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  entrega: 'bg-amber-100 text-amber-800 border-amber-300',
  capacitacion: 'bg-gray-100 text-gray-600 border-gray-300',
};
const CLASE_EPP_HINT: Record<ClaseEPP, string> = {
  registro: 'Registro de entrega firmado: acredita la entrega.',
  entrega: 'Entrega sin registro formal.',
  capacitacion: 'Capacitación sobre uso y mantención: NO acredita que se haya entregado el EPP.',
};
const matchesKeyword = (texto: string, kw: string): boolean =>
  new RegExp(`(^|[^a-z0-9])${kw}([^a-z0-9]|$)`).test(texto);
const isRestrictedDoc = (doc: any): boolean => {
  // Los cursos/certificaciones quedan disponibles para todos, aunque su tipo contenga "certificado".
  if (isCourseDoc(doc)) return false;
  const tipo = normalizeForMatch(doc?.tipo_documento || '');
  // `tipo_documento` es el campo que clasifica el documento: manda sobre el nombre libre.
  if (RESTRICTED_TIPO_KEYWORDS.some(kw => matchesKeyword(tipo, kw))) return true;
  // S\u00f3lo si el tipo no dice nada \u00fatil se cae al nombre (ej. tipo "otro" con nombre "Liquidaci\u00f3n de sueldo").
  if (!TIPOS_GENERICOS.has(tipo)) return false;
  const nombre = normalizeForMatch(doc?.nombre_documento || '');
  return RESTRICTED_TIPO_KEYWORDS.some(kw => matchesKeyword(nombre, kw));
};
// Mensaje \u00fanico para cuando la UI oculta acciones por restricci\u00f3n de rol.
const RESTRICTED_HINT = 'Documento restringido: s\u00f3lo admin o supervisor puede verlo o modificarlo.';


interface PersonalDetailModalProps {
  personal: Personal | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  prereqDetailInfo?: any | null;
  prereqDetailLoading?: boolean;
  prereqDetailError?: string | null;
  showOnlyPrerequisitos?: boolean;
  clienteName?: string;
  clienteId?: number | null;
}

type PrereqMatch = { id: number | string; tipo?: string; nombre?: string };
type MatchesMapItem = { missing: any; matches: PrereqMatch[] };

// Avisa a las páginas abiertas (Visualización, etc.) que cambiaron los documentos o los match
// manuales de esta persona, para que refresquen SOLO su cobertura en todos los clientes en vez
// de recargar todo. A diferencia de 'prereqDetailUpdated', que lleva el detalle de UN cliente,
// este evento solo trae el rut: cada página pide lo que necesite.
const avisarCambioCoberturaPersona = (rut: string | undefined | null) => {
  if (!rut) return;
  try {
    window.dispatchEvent(new CustomEvent('coberturaPersonaCambio', { detail: { rut: String(rut) } }));
  } catch { /* sin window (tests): nada que avisar */ }
};

// Motivo legible de un borrado fallido. apiService.deleteDocumentoWithClones envuelve el error
// de axios en `original`; antes el toast decía siempre "Intenta nuevamente", incluso ante un 403
// que no se arregla reintentando.
const motivoErrorBorrado = (err: any, generico = 'Error al eliminar el documento. Intenta nuevamente.') => {
  const resp = err?.original?.response || err?.response;
  if (resp?.status === 403) return 'No tienes permiso para eliminar documentos (falta el permiso "documents.delete" en tu rol).';
  if (resp?.status === 401) return 'Tu sesión expiró. Vuelve a iniciar sesión e inténtalo de nuevo.';
  if (resp?.status === 404) return 'El documento ya no existe (quizá se eliminó en otra pestaña).';
  if (!resp && err?.original?.request) return 'No hubo respuesta del servidor. Revisa que el backend esté corriendo.';
  return resp?.data?.message || generico;
};

export const PersonalDetailModal: React.FC<PersonalDetailModalProps> = ({ 
  personal, 
  isOpen, 
  onClose, 
  onUpdate, 
  prereqDetailInfo, 
  prereqDetailLoading, 
  prereqDetailError, 
  showOnlyPrerequisitos, 
  clienteName 
  , clienteId
}) => {
  const isMobile = useIsMobile();
  const { user: authUser } = useAuthStore();
  const canAccessPrivileged = hasPermission(authUser, PERMISSIONS.DOCUMENTS_DELETE);
  // Permiso para ver documentos restringidos: `admin` y `supervisor` pueden verlos todos.
  // Usuarios normales sólo pueden ver documentos no restringidos.
  const canViewRestricted = hasPermission(authUser, PERMISSIONS.DOCUMENTS_RESTRICTED_VIEW);

  const [activeTab, setActiveTab] = useState<'documentos' | 'cursos' | 'detalles'>('detalles');
  const [showSubirModal, setShowSubirModal] = useState(false);
  const [showCursoModal, setShowCursoModal] = useState(false);
  const [editingCurso, setEditingCurso] = useState<any | null>(null);
  const [showCourseDocumentModal, setShowCourseDocumentModal] = useState(false);
  const [selectedCurso, setSelectedCurso] = useState<any | null>(null);
  const [showEditDocumentModal, setShowEditDocumentModal] = useState(false);
  const [editingDocumento, setEditingDocumento] = useState<any | null>(null);
  const [editingDocumentoContext, setEditingDocumentoContext] = useState<{ clienteId: number | null; prereqId: number | string | null } | null>(null);
  const [showCloneDocumentModal, setShowCloneDocumentModal] = useState(false);
  const [cloningDocumento, setCloningDocumento] = useState<any | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDocumento, setPreviewDocumento] = useState<any | null>(null);
  const [selectedDocs, setSelectedDocs] = useState<Set<number | string>>(new Set());
  const [expandedDocs, setExpandedDocs] = useState<Set<number | string>>(new Set());
  // Historial de versiones anteriores por documento (GET /documentos/:id/historial),
  // cargado bajo demanda al presionar el botón "Historial" de cada documento.
  const [historialByDoc, setHistorialByDoc] = useState<Record<string, { loading: boolean; items: any[]; error?: string }>>({});
  const [showAllCourses, setShowAllCourses] = useState(false);
  const [showAllPersonalDocs, setShowAllPersonalDocs] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [prefillClientId, setPrefillClientId] = useState<number | null>(null);
  const [prefillPrereqId, setPrefillPrereqId] = useState<number | string | null>(null);
  const [prefillNombreDocumento, setPrefillNombreDocumento] = useState<string | undefined>(undefined);
  const [prefillTipoDocumento, setPrefillTipoDocumento] = useState<string | undefined>(undefined);
  // Documento vigente que se reemplaza desde "Estado de Requisitos" (pasa al historial al subir).
  const [prefillReemplaza, setPrefillReemplaza] = useState<{ id: number; nombre: string } | null>(null);
  const [prereqDetailOverride, setPrereqDetailOverride] = useState<any | null>(null);
  // Clientes donde la persona tiene servicios/acreditaciones
  const [clientesServicios, setClientesServicios] = useState<any[]>([]);
  const [clientesServiciosLoading, setClientesServiciosLoading] = useState(false);
  // Requisitos del cliente "Belray" (obligatorios para todos), cargados aparte para
  // mostrarlos SIEMPRE en el recuadro rojo "Documentos Belray".
  const [belrayPrereqData, setBelrayPrereqData] = useState<any | null>(null);
  const [belrayPrereqError, setBelrayPrereqError] = useState<string | null>(null);
  const [belrayClienteId, setBelrayClienteId] = useState<number | null>(null);
  // Contador para forzar la recarga de la cobertura Belray tras subir/editar/eliminar un
  // documento o cambiar un match: si no, la tarjeta se queda con datos viejos y sus
  // botones apuntan a un documento que ya no existe.
  const [belrayReloadKey, setBelrayReloadKey] = useState(0);
  // Minimizar/expandir las cajas de la columna derecha.
  const [belrayExpandido, setBelrayExpandido] = useState(true);
  const [documentosExpandido, setDocumentosExpandido] = useState(true);
  const [cursosExpandido, setCursosExpandido] = useState(true);
  // Documento cuyo historial de versiones se está viendo. La ventana es la misma para todas
  // las tarjetas (Belray, Documentación Personal y Cursos): antes cada una lo desplegaba en
  // línea y con varias versiones empujaba el resto de la lista hacia abajo, había que ir
  // cerrando tarjetas para no perderse, y no se podía exportar.
  // El estado vive aquí, junto al resto: este componente tiene un `return null` a mitad de
  // archivo y todo hook debe quedar antes.
  const [historialDoc, setHistorialDoc] = useState<any | null>(null);
  const [exportandoVersiones, setExportandoVersiones] = useState(false);
  // Filtro por área responsable (RRHH/Prevención/N/A) para identificar rápido a quién
  // le corresponde revisar cada documento — mismo catálogo que Estado de Documentación.
  const [areaFilterDocs, setAreaFilterDocs] = useState<'todas' | PrereqArea | 'sin_area'>('todas');
  // Buscador de texto para la lista de Documentación Personal (nombre, tipo o descripción).
  const [docSearchTerm, setDocSearchTerm] = useState('');
  // Mismo filtro por área, pero aplicado a la tarjeta "Documentos Belray" (independiente del de arriba).
  const [areaFilterBelray, setAreaFilterBelray] = useState<'todas' | PrereqArea | 'sin_area'>('todas');
  const [selectedClienteId, setSelectedClienteId] = useState<number | null>(null);
  const [prereqCollapsed, setPrereqCollapsed] = useState<boolean>(false);
  
  // Cliente activo para acciones contextuales: puede venir por prop (cuando el modal
  // se abre desde un cliente), por selección interna (`selectedClienteId`) o por
  // prefill (`prefillClientId`) cuando se dispara la subida de documento.
  const clienteActivoId = clienteId ?? selectedClienteId ?? prefillClientId ?? null;

  // ── Match Manual ──────────────────────────────────────────────────────────
  const [matchManualOpen, setMatchManualOpen] = useState<{ prereqId: number | string; prereqNombre: string; isEditing?: boolean; currentDocId?: string | number; clienteId?: number | string | null } | null>(null);
  const [matchManualCandidatos, setMatchManualCandidatos] = useState<any[]>([]);
  const [matchManualLoading, setMatchManualLoading] = useState(false);
  const [matchManualSaving, setMatchManualSaving] = useState(false);
  const [matchManualSelectedDoc, setMatchManualSelectedDoc] = useState<string>('');
  const [matchManualSavingDocId, setMatchManualSavingDocId] = useState<string | null>(null);
  const [matchManualSearch, setMatchManualSearch] = useState<string>('');
  // Asociar un documento a un requisito ya no crea una fila por cliente: marca el TIPO del
  // documento, y con eso acredita en todos los clientes que pidan lo mismo. Si el documento ya
  // tenía otro tipo, se le pisa sin preguntar (decisión de Claudio, 2026-10-21). Si el requisito
  // no tiene tipo, ya no se pide tipificarlo (decisión de Claudio, 2026-10-13): el backend asocia
  // el documento directo, solo para ese cliente.
  // Matches guardados localmente para persistir entre re-fetches del backend
  const localMatchOverridesRef = useRef<Record<string, any>>({});
  const location = useLocation();
  const isInAcreditacion = String(location?.pathname || window.location.pathname || '').includes('/acreditacion');

  const applyLocalOverrides = (data: any): any => {
    const overrides = localMatchOverridesRef.current;
    if (!data || Object.keys(overrides).length === 0) return data;
    const faltantes: any[] = Array.isArray(data.documentos_faltantes) ? [...data.documentos_faltantes] : [];
    const cubiertos: any[] = Array.isArray(data.prerequisitos_cubiertos) ? [...data.prerequisitos_cubiertos] : [];
    let newFaltantes = faltantes;
    let newCubiertos = cubiertos;

    for (const [pId, nuevoCubierto] of Object.entries(overrides)) {
      const yaEnCubiertos = newCubiertos.findIndex((c: any) => String(c.prerequisito_id) === pId);
      if (yaEnCubiertos >= 0) {
        // Ya está cubierto: solo asegurar el flag match_manual
        newCubiertos[yaEnCubiertos] = {
          ...newCubiertos[yaEnCubiertos],
          ...nuevoCubierto,
          match_manual: true,
        };
        continue;
      }
      const yaEnFaltantes = newFaltantes.some((p: any) => String(p.prerequisito_id ?? p.id) === pId);
      if (yaEnFaltantes) {
        // No estaba cubierto: mover de faltantes a cubiertos
        newFaltantes = newFaltantes.filter((p: any) => String(p.prerequisito_id ?? p.id) !== pId);
        newCubiertos = [...newCubiertos, nuevoCubierto];
      }
      // Si el prerequisito no está en ninguna de las dos listas, es un match manual huérfano
      // (apunta a un prereq_id que ya no existe para este cliente) — se ignora para no inflar el total.
    }

    // Derivar contadores desde los arrays finales para mantener consistencia.
    // NO calcular incrementalmente: prereq_total debe reflejar cubiertos + faltantes reales.
    const newTotal = newCubiertos.length + newFaltantes.length;
    return {
      ...data,
      prerequisitos_cubiertos: newCubiertos,
      documentos_faltantes: newFaltantes,
      prereq_covered_count: newCubiertos.length,
      prereq_total: newTotal,
      prereq_percent: newTotal > 0 ? Math.round((newCubiertos.length / newTotal) * 100) : 0,
    };
  };

  const openMatchManual = async (prereqId: number | string, prereqNombre: string, isEditing = false, currentDocId?: string | number, clienteIdOverride?: number | string | null) => {
    if (!personal?.rut) {
      toast.error('No se puede abrir match manual sin un RUT de persona');
      return;
    }
    const activeClienteId = clienteIdOverride ?? clienteActivoId;
    if (!activeClienteId) {
      toast.error('No se puede hacer match manual sin un cliente seleccionado. Selecciona un cliente o abre el modal desde un cliente.');
      return;
    }
    console.log('🔍 Match Manual - Abriendo para:', { prereqId, prereqNombre, clienteId: activeClienteId, rut: personal.rut, isEditing, currentDocId });

    setMatchManualOpen({ prereqId, prereqNombre, isEditing, currentDocId, clienteId: activeClienteId });
    setMatchManualSearch('');
    setMatchManualSelectedDoc('');
    setMatchManualSavingDocId(null);
    setMatchManualCandidatos([]);
    await cargarCandidatosMatch(activeClienteId, prereqId);
  };

  /** Candidatos del trabajador para un requisito, con el tipo de cada uno. */
  const cargarCandidatosMatch = async (activeClienteId: number | string, prereqId: number | string) => {
    if (!personal?.rut) return;
    setMatchManualLoading(true);
    try {
      console.log('🔍 Match Manual - Llamando a getMatchManualCandidatos...');
      const res = await apiService.getMatchManualCandidatos(Number(activeClienteId), personal.rut, prereqId);
      console.log('✅ Match Manual - Respuesta del backend:', res);
      const docs: any[] = res?.data ?? res ?? [];
      console.log('📦 Match Manual - Documentos candidatos:', docs.length);

      const candidates: any[] = Array.isArray(docs) ? docs : [];

      const enriched = candidates.map((c: any) => {
        // Prefer explicit display fields coming from the candidatos API
        const directName = c?.nombre_archivo || c?.nombre_original || c?.nombre_documento || c?.nombre || c?.tipo_original || c?.tipo_documento || c?.tipo_normalizado;
        if (directName) return { ...c, _displayName: String(directName) };

        // Try to match with local documentos del RUT using id or normalized filename
        let matched: any = null;
        const candidateId = String(c?.id ?? c?.documento_id ?? '');
        if (candidateId) {
          matched = todosDocumentos.find(d => String(d.id) === candidateId || String(d.documento_id) === candidateId);
        }

        if (!matched) {
          const candName = String(c?.nombre_archivo || c?.nombre_original || c?.nombre || '');
          if (candName) {
            const candNorm = normalizeName(candName);
            matched = todosDocumentos.find(d => normalizeName(d?.nombre_original || d?.nombre_archivo || d?.nombre || '') === candNorm);
          }
        }

        const displayFromMatched = matched ? (matched.nombre_original || matched.nombre_archivo || matched.nombre || matched.tipo_documento || matched.tipo_original) : null;
        return { ...c, _displayName: displayFromMatched || `Doc #${candidateId || 'unknown'}` };
      });

      setMatchManualCandidatos(enriched);
    } catch (error: any) {
      console.error('❌ Match Manual - Error completo:', error);
      console.error('❌ Match Manual - Status:', error?.response?.status);
      console.error('❌ Match Manual - Data:', error?.response?.data);
      const errorMsg = error?.response?.data?.mensaje || error?.response?.data?.message || error?.message || 'No se pudieron cargar los documentos candidatos';
      toast.error(errorMsg);
      console.error('Error en getMatchManualCandidatos:', error);
    } finally {
      setMatchManualLoading(false);
    }
  };

  /** Un click en la tarjeta de documento ejecuta el match directamente */
  const guardarMatchManual = async (docIdParam?: string) => {
    const docId = docIdParam ?? matchManualSelectedDoc;

    if (!personal?.rut) {
      toast.error('No se puede guardar match manual sin un RUT de persona');
      return;
    }
    const activeClienteId = matchManualOpen?.clienteId ?? clienteActivoId;
    if (!activeClienteId) {
      toast.error('No se puede guardar match manual sin un cliente seleccionado');
      return;
    }
    if (!matchManualOpen) {
      toast.error('No hay prerrequisito seleccionado para el match');
      return;
    }
    if (!docId) {
      toast.error('Debe seleccionar un documento');
      return;
    }

    // Un click asocia, sin confirmación ni avisos aunque el documento ya tenga otro tipo
    // (decisión de Claudio, 2026-10-21): el backend fuerza el tipo del requisito igual.
    setMatchManualSaving(true);
    setMatchManualSavingDocId(docId);
    try {
      await apiService.crearMatchManual({
        cliente_id: Number(activeClienteId),
        rut: personal.rut,
        documento_id: docId,
        prereq_id: matchManualOpen.prereqId,
      });
      toast.success('Documento asociado al requisito');
      // Capturar datos antes de limpiar el estado del modal
      const capturedMatchOpen = matchManualOpen;
      const capturedCandidatos = [...matchManualCandidatos];
      const capturedDocId = docId;
      setMatchManualOpen(null);
      setMatchManualSavingDocId(null);
      
      // Crear el objeto de cubierto con match_manual: true
      const docSeleccionado = capturedCandidatos.find((c: any) => String(c.id ?? c.documento_id) === String(capturedDocId));
      const nuevoCubierto = {
        prerequisito_id: capturedMatchOpen.prereqId,
        prerequisito_tipo: capturedMatchOpen.prereqNombre,
        prerequisito_descripcion: capturedMatchOpen.prereqNombre,
        documento_id: capturedDocId,
        documento_tipo_original: docSeleccionado?.tipo_documento || docSeleccionado?.tipo_original || null,
        documento_tipo_normalizado: docSeleccionado?.tipo_normalizado || null,
        documento_vencido: false,
        match_manual: true,
      };
      
      // Recargar datos de prerrequisitos para reflejar los cambios
      refreshBelrayPrereqs();
      let freshData: any = null;
      try {
        freshData = await fetchMergedPrereqsCached(Number(activeClienteId), personal.rut, { fresh: true });
      } catch { /* usar datos anteriores si falla */ }

      // Lo que muestra la ficha es lo que decidió el backend (puede cubrir un automático en vez
      // del manual recién guardado). El override local solo sostiene la vista si la recarga falló.
      const overridesSinEste = { ...localMatchOverridesRef.current };
      delete overridesSinEste[String(capturedMatchOpen.prereqId)];
      localMatchOverridesRef.current = freshData
        ? overridesSinEste
        : { ...overridesSinEste, [String(capturedMatchOpen.prereqId)]: nuevoCubierto };

      const baseData = freshData || prereqDetailOverride || prereqDetailInfo || {};
      // Un match hecho desde la tarjeta Belray usa el cliente Belray: no debe pisar el
      // detalle del cliente que está seleccionado en la ficha.
      if (String(activeClienteId) === String(clienteActivoId)) {
        setPrereqDetailOverride(applyLocalOverrides(baseData));
      }
      // Informar al padre para que actualice la vista (ej. VisualizacionPage actualizar cobertura)
      try {
        const emitted = applyLocalOverrides(baseData);
        window.dispatchEvent(new CustomEvent('prereqDetailUpdated', { detail: { rut: personal.rut, clienteId: activeClienteId, detail: emitted } }));
      } catch (e) {
        console.warn('Error emitiendo evento prereqDetailUpdated tras guardar match manual:', e);
      }
      avisarCambioCoberturaPersona(personal?.rut);
      try {
        if (typeof onUpdate === 'function') await onUpdate();
      } catch (e) {
        console.warn('Error llamando onUpdate tras guardar match manual:', e);
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.mensaje || error?.response?.data?.message || error?.message || 'Error al guardar el match manual';
      toast.error(errorMsg, { autoClose: 6000 });
      console.error('Error al guardar match manual:', error);
      setMatchManualSavingDocId(null);
    } finally {
      setMatchManualSaving(false);
    }
  };

  // `clienteIdOverride` permite quitar el match desde la tarjeta Belray, que trabaja
  // siempre contra el cliente Belray y no contra el cliente seleccionado en la ficha.
  const eliminarMatchManual = async (prereqId: number | string, clienteIdOverride?: number | string | null) => {
    if (!personal?.rut) {
      toast.error('No se puede eliminar match manual sin un RUT de persona');
      return;
    }
    const activeClienteId = clienteIdOverride ?? clienteActivoId;
    if (!activeClienteId) {
      toast.error('No se puede eliminar match manual sin un cliente seleccionado');
      return;
    }

    try {
      // "Quitar match" = dejar el requisito en blanco. El rechazo persiste, así que
      // también bloquea el match automático (que se recalcula en cada fetch y volvería
      // a cubrir el requisito). El backend borra el match manual como parte del rechazo.
      try {
        await apiService.rechazarMatch({ cliente_id: Number(activeClienteId), rut: personal.rut, prereq_id: prereqId });
      } catch (err: any) {
        // Backend sin el endpoint de rechazo todavía: al menos borrar el match manual.
        if (err?.response?.status !== 404) throw err;
        await apiService.eliminarMatchManual({ cliente_id: Number(activeClienteId), rut: personal.rut, prereq_id: prereqId });
      }
      // Limpiar del ref local para que no se re-aplique en el siguiente fetch
      const updatedOverrides = { ...localMatchOverridesRef.current };
      delete updatedOverrides[String(prereqId)];
      localMatchOverridesRef.current = updatedOverrides;
      refreshBelrayPrereqs();
      const fresh = await fetchMergedPrereqsCached(Number(activeClienteId), personal.rut, { fresh: true });
      // Sólo pisar el detalle en pantalla si corresponde al mismo cliente que se está viendo.
      if (String(activeClienteId) === String(clienteActivoId)) {
        setPrereqDetailOverride(applyLocalOverrides(fresh));
      }
      // Verificar que el rechazo realmente lo dejó en blanco: si el backend lo sigue
      // reportando cubierto, avisar en vez de dar por hecho que quedó vacío.
      const sigueCubierto = (Array.isArray(fresh?.prerequisitos_cubiertos) ? fresh.prerequisitos_cubiertos : [])
        .some((c: any) => String(c?.prerequisito_id) === String(prereqId));
      if (sigueCubierto) {
        toast.info('El requisito sigue apareciendo cubierto. Si el match es automático, revisa que el backend tenga el rechazo aplicado.', { autoClose: 8000 });
      } else {
        toast.success('Match quitado: el requisito quedó en blanco (el documento no se eliminó)');
      }
      try {
        const emitted = applyLocalOverrides(fresh);
        window.dispatchEvent(new CustomEvent('prereqDetailUpdated', { detail: { rut: personal.rut, clienteId: activeClienteId, detail: emitted } }));
      } catch (e) {
        console.warn('Error emitiendo evento prereqDetailUpdated tras eliminar match manual:', e);
      }
      avisarCambioCoberturaPersona(personal?.rut);
      try {
        if (typeof onUpdate === 'function') await onUpdate();
      } catch (e) {
        console.warn('Error llamando onUpdate tras eliminar match manual:', e);
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.mensaje || error?.response?.data?.message || error?.message || 'Error al eliminar el match manual';
      toast.error(errorMsg);
      console.error('Error al eliminar match manual:', error);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
      const data = prereqDetailOverride || prereqDetailInfo;
      if (!data) return;
      try {
        cloudRunLog('Detail modal data', {
          covered: data.prereq_covered_count,
          total: data.prereq_total,
          missingListSize: Array.isArray(data.documentos_faltantes) ? data.documentos_faltantes.length : undefined,
          coveredListSize: Array.isArray(data.prerequisitos_cubiertos) ? data.prerequisitos_cubiertos.length : undefined,
          sampleMissing: Array.isArray(data.documentos_faltantes) ? data.documentos_faltantes.slice(0, 1) : undefined
        });
      } catch {}
    }, [prereqDetailOverride, prereqDetailInfo]);

  // Cuando prereqDetailInfo llega por prop (desde el padre), aplicar los overrides locales
  useEffect(() => {
    if (!prereqDetailInfo) return;
    if (Object.keys(localMatchOverridesRef.current).length > 0) {
      setPrereqDetailOverride(applyLocalOverrides(prereqDetailInfo));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prereqDetailInfo]);
  const [prereqPage, setPrereqPage] = useState<number>(1);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ show: boolean; docId: number | null; hasClones: boolean; totalClones: number; clones: any[] }>({
    show: false,
    docId: null,
    hasClones: false,
    totalClones: 0,
    clones: []
  });
  // Documento en proceso de borrado (para deshabilitar su botón y evitar doble-click mientras
  // corre el request — antes no había ningún indicio visual de que el click se hubiera registrado).
  const [deletingDocId, setDeletingDocId] = useState<number | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<UpdatePersonalData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentEstadoId, setCurrentEstadoId] = useState<number>(personal?.estado_id || 1);
  const [showAsignaciones, setShowAsignaciones] = useState(false);
  const [showChangeDataModal, setShowChangeDataModal] = useState(false);
  const [showCentroMassModal, setShowCentroMassModal] = useState(false);

  const [availableCentros, setAvailableCentros] = useState<Array<{ id: number | string; nombre: string }>>([]);
  const [selectedCentroId, setSelectedCentroId] = useState<number | string | null>(null);
  const [centrosTree, setCentrosTree] = useState<any[]>([]);

  // ── Acreditación por cliente ──────────────────────────────────────────────
  // Solo se permiten clientes de estas carteras
  const ACRED_ALLOWED_CARTERAS = ['CAROZZI', 'CEMENTERAS', 'SNACK', 'ARIZTIA'];
  const ACRED_PAGE_SIZE = 25;

  const [acredOpen, setAcredOpen] = useState(false);
  /** Todos los clientes de las carteras permitidas (cargados una sola vez) */
  const [acredAllClientes, setAcredAllClientes] = useState<any[]>([]);
  /** Vista paginada del resultado filtrado */
  const [acredClientes, setAcredClientes] = useState<any[]>([]);
  const [acredResumen, setAcredResumen] = useState<{ acreditados: number; total_clientes: number } | null>(null);
  const [acredLoading, setAcredLoading] = useState(false);
  const [acredSearch, setAcredSearch] = useState('');
  const [acredCarteraFiltro, setAcredCarteraFiltro] = useState('');
  const [acredEstadoFiltro, setAcredEstadoFiltro] = useState<'todos' | 'acreditados' | 'pendientes'>('todos');
  const [acredPage, setAcredPage] = useState(1);
  const [acredTotalPages, setAcredTotalPages] = useState(1);
  const [acredTotal, setAcredTotal] = useState(0);
  /** clienteId:string → nuevo estado deseado (true=acreditar, false=revocar) — no guardado aún */
  const [acredPendingChanges, setAcredPendingChanges] = useState<Record<string, boolean>>({});
  const [acredSaving, setAcredSaving] = useState(false);
  // ─────────────────────────────────────────────────────────────────────────

  // Estado local para los datos actualizados del personal
  const [currentPersonal, setCurrentPersonal] = useState<Personal | null>(personal);
  
  // State for profile image
  const [imageError, setImageError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const queryClient = useQueryClient();
  const updateMutation = useUpdatePersonal();
  const updatePersonalDataMutation = useUpdatePersonalData();
  const updateNombreMutation = useUpdateNombre();
  const { data: estadosData, isLoading: estadosLoading } = useEstados({ limit: 100 });
  const { data: documentosData, isLoading: isLoadingDocumentos, error: errorDocumentos, refetch: refetchDocumentos } = useDocumentosByPersona(personal?.rut ?? '');
  const { data: cursosData, isLoading: isLoadingCursos, error: errorCursos, refetch: refetchCursos } = useCursosByRut(personal?.rut ?? '');
  const { getArea } = useAreaCatalog();
  const deleteCursoMutation = useDeleteCurso();
  const deleteDocumentoWithClonesMutation = useDeleteDocumentoWithClones();
  const downloadDocumentoMutation = useDownloadDocumento();
  const { profileImage: hookProfileImage, hasImage: hookHasImage, uploadImage, deleteImage, refetch: refetchProfileImage, isUploading: isUploadingImage, isDeleting: isDeletingImage } = useProfileImage(personal?.rut || '');
  const updateDocumentoMutation = useUpdateDocumento();

  // Refetch documentos y cursos cada vez que el modal abre con un RUT válido
  // Evita que React Query sirva datos cacheados vacíos de intentos previos fallidos
  useEffect(() => {
    if (isOpen && personal?.rut) {
      refetchDocumentos();
      refetchCursos();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, personal?.rut]);

  // Cargar lista de clientes/servicios desde el servicio de Servicios
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setClientesServiciosLoading(true);
      try {
        const res: any = await apiService.getClientes({ limit: 1000, simple: true, visible_en_chart: true });
        const raw: any[] = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);
        if (cancelled) return;

        // Fuente única: mostrar exactamente los clientes seleccionados como visibles en
        // Visualización (visible_en_chart === true). Deduplicar por id.
        const seen = new Set<number>();
        const mapped = (raw || [])
          .filter((c: any) => c.visible_en_chart === true)
          .map((c: any) => ({
            cliente_id: c.id ?? c.cliente_id,
            cliente_nombre: c.nombre ?? c.cliente_nombre ?? c.nombre_cliente ?? String(c.id)
          }))
          .filter((item: any) => {
            if (seen.has(item.cliente_id)) return false;
            seen.add(item.cliente_id);
            return true;
          });

        setClientesServicios(mapped);
      } catch (e) {
        console.error('Error cargando clientes de servicios:', e);
        if (!cancelled) setClientesServicios([]);
      } finally {
        if (!cancelled) setClientesServiciosLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (personal?.estado_id !== undefined) {
      setCurrentEstadoId(personal.estado_id);
    }
    // Actualizar currentPersonal cuando cambia el prop personal
    setCurrentPersonal(personal);
    // Reset image error when personal changes
    setImageError(false);
  }, [personal]);

  // Flatten tree para búsquedas rápidas
  const flatCentros = useMemo(() => {
    const flatten = (nodes: any[]): any[] => {
      let result: any[] = [];
      nodes.forEach(n => {
        result.push({ id: n.id_centro_costo || n.id, nombre: n.nombre });
        if (n.children && n.children.length > 0) result.push(...flatten(n.children));
      });
      return result;
    };
    return centrosTree.length > 0 ? flatten(centrosTree) : (availableCentros || []);
  }, [centrosTree, availableCentros]);

  // Organizar centros en grupos por prefijo (43xxx = Codelco, 44xxx = Servicios/IAC)
  const centrosGrouped = useMemo(() => {
    if (flatCentros.length === 0) return [];

    const getPrefix = (id: string) => String(id).substring(0, 2);
    const groups = new Map<string, { label: string; centros: any[] }>();

    flatCentros.forEach(c => {
      const prefix = getPrefix(c.id);
      let label = 'Otros';
      if (prefix === '43') label = 'Codelco (43xxx)';
      else if (prefix === '44') label = 'Servicios/IAC (44xxx)';

      if (!groups.has(prefix)) groups.set(prefix, { label, centros: [] });
      groups.get(prefix)!.centros.push(c);
    });

    const result = Array.from(groups.values());
    result.sort((a, b) => {
      const order = { 'Codelco (43xxx)': 0, 'Servicios/IAC (44xxx)': 1, 'Otros': 2 };
      return (order[a.label as keyof typeof order] ?? 99) - (order[b.label as keyof typeof order] ?? 99);
    });
    return result;
  }, [flatCentros]);

  // Inicializar y cargar centros disponibles
  useEffect(() => {
    setSelectedCentroId((personal as any)?.id_centro_costo ?? null);
    let cancelled = false;
    (async () => {
      try {
        // Preferir centrosStatic si existe (ya organizados)
        if (Array.isArray(centrosStatic) && centrosStatic.length > 0) {
          setAvailableCentros(centrosStatic as any);
          // Intentar cargar tree en background para versión jerárquica
          try {
            const tree: any[] = await apiService.getCentrosCostoTree();
            if (!cancelled && Array.isArray(tree) && tree.length > 0) {
              setCentrosTree(tree);
            }
          } catch {
            // Ignorar error de tree, ya tenemos centrosStatic
          }
          return;
        }

        // Fallback: API
        const res: any = await apiService.getClientes({ limit: 1000, simple: true });
        const raw: any[] = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);
        if (cancelled) return;
        const mapped = raw.map((c: any) => ({ id: c.id ?? c.cliente_id, nombre: c.nombre ?? c.cliente_nombre ?? String(c.id) }));
        setAvailableCentros(mapped.slice(0, 1000));
      } catch (e) {
        console.error('No se pudieron cargar centros:', e);
        if (!cancelled) setAvailableCentros([]);
      }
    })();
    return () => { cancelled = true; };
  }, [personal, isOpen]);

  // ── Cargar acreditaciones cuando el panel se abre ──────────────────────
  /** Aplica filtro de búsqueda, cartera y estado, luego pagina sobre acredAllClientes */
  const applyAcredFilter = (all: any[], search: string, page: number, cartera = acredCarteraFiltro, estado = acredEstadoFiltro) => {
    const q = search.toLowerCase().trim();
    const filtered = all.filter(c => {
      if (q && !(c.cliente_nombre || '').toLowerCase().includes(q)) return false;
      if (cartera && (c.cartera_nombre || '').toUpperCase() !== cartera.toUpperCase()) return false;
      if (estado === 'acreditados' && !c.acreditado) return false;
      if (estado === 'pendientes' && !!c.acreditado) return false;
      return true;
    });
    const total = filtered.length;
    const totalPages = Math.max(1, Math.ceil(total / ACRED_PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    setAcredTotal(total);
    setAcredTotalPages(totalPages);
    setAcredPage(safePage);
    setAcredClientes(filtered.slice((safePage - 1) * ACRED_PAGE_SIZE, safePage * ACRED_PAGE_SIZE));
  };

  /** Carga TODAS las páginas del API y filtra solo las carteras permitidas */
  const loadAcredData = async () => {
    if (!personal?.rut) return;
    setAcredLoading(true);
    try {
      // Página 1 para conocer total de páginas
      const first = await apiService.getAcreditacionPersonal(personal.rut, { page: 1 }) as any;
      if (!first?.success) return;

      const totalPages: number = first.paginacion?.paginas ?? 1;
      let all: any[] = [...(first.clientes ?? [])];

      // Cargar páginas restantes en paralelo
      if (totalPages > 1) {
        const pageNums = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const results = await Promise.all(
          pageNums.map(p => apiService.getAcreditacionPersonal(personal.rut!, { page: p }) as any)
        );
        for (const r of results) {
          if (r?.success) all = [...all, ...(r.clientes ?? [])];
        }
      }

      // Filtrar a los clientes visibles en Visualización (mismo criterio que el dropdown "Clientes con servicios").
      // Fallback a keyword si aún no se cargó la lista de clientes visibles.
      const visibleIds = new Set((clientesServicios || []).map((c: any) => Number(c.cliente_id ?? c.id)));
      const filtered = visibleIds.size > 0
        ? all.filter(c => visibleIds.has(Number(c.cliente_id ?? c.id)))
        : all.filter(c => esClienteServicio(c.cliente_nombre || ''));

      setAcredAllClientes(filtered);
      // Resumen: solo dentro de las carteras permitidas
      const acreditados = filtered.filter(c => !!c.acreditado).length;
      setAcredResumen({ acreditados, total_clientes: filtered.length });
      applyAcredFilter(filtered, acredSearch, 1);
    } catch (e) {
      console.error('Error cargando acreditaciones', e);
    } finally {
      setAcredLoading(false);
    }
  };

  useEffect(() => {
    if (acredOpen && personal?.rut) {
      loadAcredData();
    }
    // Al cerrar el panel se descartan los cambios pendientes no guardados
    if (!acredOpen) {
      setAcredPendingChanges({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acredOpen, personal?.rut]);

    // NOTE: intentionally do NOT auto-select a cliente on open — start with blank selection
    // and load requisitos only when the user selects a cliente via the UI.

    // Las asociaciones manuales ya NO se cargan aparte para pisar el detalle (2026-10-21): el
    // backend decide (el automático manda sobre el manual, y un manual vencido no cubre) y marca
    // `match_manual` en cada cubierto. Pisarlo mostraba "manual" donde cubría el automático y
    // daba por cumplido un requisito cuyo documento manual había vencido.

  // Helper: obtiene prerrequisitos del backend y los completa con la lista configurada del cliente
  // El catálogo de requisitos CONFIGURADOS de un cliente no depende de qué persona se esté
  // viendo — es la misma respuesta para cualquiera. Sin este cache, abrir la ficha de 5
  // personas distintas del mismo cliente pedía ese catálogo 5 veces seguidas.
  const clientePrereqCatalogCacheRef = useRef<Map<number, ReturnType<typeof apiService.getClientePrerrequisitosByClienteId>>>(new Map());
  const getClientePrerrequisitosCached = (cId: number) => {
    const cached = clientePrereqCatalogCacheRef.current.get(cId);
    if (cached) return cached;
    const promise = apiService.getClientePrerrequisitosByClienteId(cId).catch(err => {
      clientePrereqCatalogCacheRef.current.delete(cId);
      throw err;
    });
    clientePrereqCatalogCacheRef.current.set(cId, promise);
    return promise;
  };

  const fetchMergedPrereqs = async (cId: number, rut: string): Promise<any> => {
    const [res, configRes] = await Promise.allSettled([
      apiService.getPrerequisitosDetailByRut(cId, rut, { page: 1, limit: 100 }),
      getClientePrerrequisitosCached(cId),
    ]);
    // Si el detalle falla NO se puede saber qué está cubierto. Antes se seguía con
    // `rawData = {}` y el merge de abajo listaba todos los prerrequisitos configurados
    // como faltantes: en pantalla salía "0/N requisitos · Sin documento", idéntico a un
    // resultado real. Un error de API tiene que verse como error, no como cobertura cero.
    if (res.status === 'rejected') {
      throw res.reason instanceof Error
        ? res.reason
        : new Error('No se pudo cargar el detalle de requisitos');
    }
    const detalle: any = (res.value as any)?.data ?? res.value;
    const allConfigured: any[] = configRes.status === 'fulfilled'
      ? (Array.isArray((configRes.value as any)?.data) ? (configRes.value as any).data : [])
      : [];
    // `getPrerequisitosDetailByRut` convierte un 404 en `{ success: true, data: [] }`
    // ("cliente sin requisitos configurados"), así que la promesa se cumple igual. Si el
    // catálogo SÍ trae prerrequisitos, ese vacío es una contradicción — la ruta no está
    // disponible — y el merge de abajo los listaría todos como faltantes: otra vez el
    // falso "0/N". Detectarlo por la forma: el detalle real es un objeto, no un array.
    const detalleAusente = !detalle || Array.isArray(detalle);
    if (detalleAusente && allConfigured.length > 0) {
      throw new Error(`El cliente tiene ${allConfigured.length} requisitos configurados, pero el endpoint de cobertura no devolvió detalle (ruta no disponible).`);
    }
    const rawData: any = detalle;
    if (allConfigured.length > 0 && rawData) {
      const faltantes: any[] = Array.isArray(rawData.documentos_faltantes) ? rawData.documentos_faltantes : [];
      const cubiertos: any[] = Array.isArray(rawData.prerequisitos_cubiertos) ? rawData.prerequisitos_cubiertos : [];
      const knownIds = new Set([
        ...faltantes.map((f: any) => String(f.prerequisito_id ?? f.id ?? '')),
        ...cubiertos.map((c: any) => String(c.prerequisito_id ?? '')),
      ]);
      const missing = allConfigured.filter((p: any) => p.id && !knownIds.has(String(p.id)));
      if (missing.length > 0) {
        const extraFaltantes = missing.map((p: any) => {
          // V2 usa `nombre`, legacy usa `tipo_documento`
          const nombre = p.nombre ?? p.tipo_documento ?? '';
          return {
            prerequisito_id: p.id,
            value: nombre,
            label: nombre,
            required: p.es_obligatorio ?? p.obligatorio ?? true,
            tipo_documento: nombre,
          };
        });
        rawData.documentos_faltantes = [...faltantes, ...extraFaltantes];
        const newTotal = cubiertos.length + rawData.documentos_faltantes.length;
        rawData.prereq_total = newTotal;
        rawData.prereq_covered_count = cubiertos.length;
        rawData.prereq_percent = newTotal > 0 ? Math.round((cubiertos.length / newTotal) * 100) : 0;
      }
    }
    return rawData;
  };

  // Cache en memoria de fetchMergedPrereqs por (clienteId, rut), scopeado a esta instancia
  // del modal (se pierde al desmontar). Dos casos reales que resolvía sin esto:
  //   1) el cliente activo es justo BelRay -> el efecto de "cliente seleccionado" y el efecto
  //      de BelRay (abajo) pedían la MISMA cobertura dos veces en paralelo.
  //   2) volver a un cliente ya visto en la misma sesión del modal volvía a pedir todo desde
  //      cero, aunque nada hubiera cambiado.
  // Los call sites que siguen a una mutación (subir/editar/borrar documento, match manual)
  // pasan { fresh: true }: fuerza el fetch real y ACTUALIZA la entrada cacheada, para que una
  // lectura pasiva posterior (ej. volver a este cliente) no sirva datos viejos.
  const mergedPrereqsCacheRef = useRef<Map<string, Promise<any>>>(new Map());
  const fetchMergedPrereqsCached = (cId: number, rut: string, opts?: { fresh?: boolean }): Promise<any> => {
    const key = `${cId}:${rut}`;
    if (!opts?.fresh) {
      const cached = mergedPrereqsCacheRef.current.get(key);
      if (cached) return cached;
    }
    const promise = fetchMergedPrereqs(cId, rut).catch(err => {
      mergedPrereqsCacheRef.current.delete(key); // no cachear errores
      throw err;
    });
    mergedPrereqsCacheRef.current.set(key, promise);
    return promise;
  };

    // Cuando se selecciona un cliente, cargar sus prerrequisitos para este RUT
    useEffect(() => {
      if (!isOpen) return;
      if (!selectedClienteId || !personal?.rut) {
        localMatchOverridesRef.current = {};
        setPrereqDetailOverride(null);
        return;
      }

      // Limpiar overrides del cliente anterior antes de cargar el nuevo.
      // Evita que overrides stale (de otro cliente) contaminen el render
      // si getMatchManual falla o tarda en responder.
      localMatchOverridesRef.current = {};

      let cancelled = false;
      (async () => {
        try {
          // El detalle del backend ya trae qué cubre el match manual (`match_manual`): no se
          // cargan las asociaciones aparte para pisarlo (ver más arriba).
          const rawData = await fetchMergedPrereqsCached(Number(selectedClienteId), personal.rut);
          if (!cancelled) setPrereqDetailOverride(applyLocalOverrides(rawData));
        } catch (e) {
          console.error('Error cargando prerrequisitos para cliente seleccionado:', e);
          if (!cancelled) setPrereqDetailOverride(null);
        }
      })();

      return () => { cancelled = true; };
    }, [selectedClienteId, personal?.rut, isOpen]);

  // Cargar SIEMPRE los requisitos del cliente "Belray" (independiente del cliente seleccionado).
  // Si el cliente activo es justo BelRay, fetchMergedPrereqsCached reutiliza el fetch que ya
  // disparó el otro efecto en vez de pedir la misma cobertura dos veces en paralelo.
  const prevBelrayReloadKeyRef = useRef(belrayReloadKey);
  useEffect(() => {
    if (!isOpen || !personal?.rut) { setBelrayPrereqData(null); setBelrayPrereqError(null); setBelrayClienteId(null); return; }
    const belrayCli = findBelrayCliente(clientesServicios);
    if (!belrayCli) { setBelrayPrereqData(null); setBelrayPrereqError(null); setBelrayClienteId(null); return; }
    const belrayId = Number(belrayCli.cliente_id ?? belrayCli.id);
    if (!belrayId) { setBelrayPrereqData(null); setBelrayPrereqError(null); setBelrayClienteId(null); return; }
    setBelrayClienteId(belrayId);
    // refreshBelrayPrereqs() incrementa belrayReloadKey a propósito tras una mutación — ahí SÍ
    // hace falta forzar un fetch fresco (el cache serviría el dato viejo). Si el efecto se
    // re-corre por otra razón (ej. clientesServicios cambió de referencia), se lee del cache.
    const isManualRefresh = prevBelrayReloadKeyRef.current !== belrayReloadKey;
    prevBelrayReloadKeyRef.current = belrayReloadKey;
    let cancelled = false;
    (async () => {
      try {
        const raw = await fetchMergedPrereqsCached(belrayId, personal.rut, { fresh: isManualRefresh });
        if (!cancelled) { setBelrayPrereqData(raw); setBelrayPrereqError(null); }
      } catch (e: any) {
        console.warn('No se pudieron cargar los requisitos Belray:', e);
        if (!cancelled) {
          setBelrayPrereqData(null);
          const status = e?.response?.status;
          setBelrayPrereqError(status ? `El servidor respondió ${status} al calcular la cobertura.` : (e?.message || 'Error de conexión con el servidor.'));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, personal?.rut, clientesServicios, belrayReloadKey]);

  // Marca la cobertura Belray como sucia tras cualquier cambio sobre documentos.
  const refreshBelrayPrereqs = useCallback(() => setBelrayReloadKey(k => k + 1), []);

  // Requisitos Belray normalizados (cubiertos + faltantes) para el recuadro.
  const requisitosBelray = useMemo(() => mapRequisitosBelray(belrayPrereqData), [belrayPrereqData]);
  // Vista filtrada por área responsable (RRHH/Prevención/N/A), igual que en Documentación Personal.
  const requisitosBelrayFiltered = useMemo(() => {
    if (areaFilterBelray === 'todas') return requisitosBelray;
    return requisitosBelray.filter((r: any) => {
      const area = getArea(r.tipo);
      return areaFilterBelray === 'sin_area' ? !area : area === areaFilterBelray;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requisitosBelray, areaFilterBelray, getArea]);

  const handleAcredSearchChange = (val: string) => {
    setAcredSearch(val);
    applyAcredFilter(acredAllClientes, val, 1, acredCarteraFiltro, acredEstadoFiltro);
  };

  const handleAcredCarteraChange = (val: string) => {
    setAcredCarteraFiltro(val);
    applyAcredFilter(acredAllClientes, acredSearch, 1, val, acredEstadoFiltro);
  };

  const handleAcredEstadoChange = (val: 'todos' | 'acreditados' | 'pendientes') => {
    setAcredEstadoFiltro(val);
    applyAcredFilter(acredAllClientes, acredSearch, 1, acredCarteraFiltro, val);
  };

  // Actualizar centro de costo para la persona mostrada
  const handleUpdateCentro = async () => {
    if (!currentPersonal) return toast.error('No hay persona seleccionada');
    if (selectedCentroId == null) return toast.error('Selecciona un centro');
    const centroNombre = availableCentros.find(c => String(c.id) === String(selectedCentroId))?.nombre ?? undefined;
    try {
      // Obtener datos actuales para enviar los campos requeridos por el backend
      const resp: any = await apiService.getPersonalById(currentPersonal.id || currentPersonal.rut);
      const existing: any = resp?.data ?? resp;
      const payload: any = {
        // Campos obligatorios que el backend valida
        sexo: existing?.sexo,
        fecha_nacimiento: existing?.fecha_nacimiento,
        licencia_conducir: existing?.licencia_conducir,
        cargo: existing?.cargo,
        estado_id: existing?.estado_id,
        // Mantener nombres/otros campos para evitar sobrescribir
        nombres: existing?.nombres ?? `${existing?.nombre || ''} ${existing?.apellido || ''}`.trim(),
        // Actualización del centro
        id_centro_costo: selectedCentroId,
        centro_costo: centroNombre,
      };
      await updateMutation.mutateAsync({ id: currentPersonal.id || currentPersonal.rut, data: payload });
      toast.success('Centro de costo actualizado');
      queryClient.invalidateQueries({ queryKey: ['personal', 'detail', currentPersonal.id || currentPersonal.rut] });
      queryClient.invalidateQueries({ queryKey: ['personal', 'list'] });
      setCurrentPersonal(p => p ? ({ ...p, id_centro_costo: selectedCentroId as any, centro_costo: centroNombre } as Personal) : p);
      onUpdate?.();
    } catch (e: any) {
      console.error('Error actualizando centro:', e);
      const msg = e?.response?.data?.message || e?.message || 'Error actualizando centro';
      toast.error(msg);
    }
  };

  /**
   * Marca/desmarca un cliente localmente como pendiente de guardar.
   * No llama al backend hasta que el usuario presione «Guardar acreditación».
   */
  const handleToggleAcred = (clienteId: number, estadoVisual: boolean) => {
    const key = String(clienteId);
    const original = acredAllClientes.find(c => String(c.cliente_id) === key);
    const estadoOriginal = original ? !!original.acreditado : estadoVisual;
    const nuevoEstado = !estadoVisual;
    setAcredPendingChanges(prev => {
      const next = { ...prev };
      // Si vuelve al estado original, eliminar del pendiente
      if (nuevoEstado === estadoOriginal) {
        delete next[key];
      } else {
        next[key] = nuevoEstado;
      }
      return next;
    });
  };

  /** Guarda todos los cambios pendientes en el backend mediante llamadas por lote */
  const handleGuardarAcred = async () => {
    if (!personal?.rut) return;
    const entries = Object.entries(acredPendingChanges);
    if (entries.length === 0) return;
    setAcredSaving(true);
    try {
      const toAcreditar = entries.filter(([, v]) => v).map(([k]) => Number(k));
      const toRevocar   = entries.filter(([, v]) => !v).map(([k]) => Number(k));
      if (toAcreditar.length) await apiService.acreditarEnClientes(personal.rut, { cliente_ids: toAcreditar });
      if (toRevocar.length)   await apiService.revocarClientes(personal.rut, { cliente_ids: toRevocar });

      // Actualizar el listado maestro con los estados guardados
      setAcredAllClientes(prev => {
        const snapshot = { ...acredPendingChanges };
        const next = prev.map(c => {
          const k = String(c.cliente_id);
          if (k in snapshot) {
            return {
              ...c,
              acreditado: snapshot[k],
              fecha_acreditacion: snapshot[k] ? new Date().toISOString() : null,
            };
          }
          return c;
        });
        const acreditados = next.filter(c => !!c.acreditado).length;
        setAcredResumen({ acreditados, total_clientes: next.length });
        applyAcredFilter(next, acredSearch, acredPage, acredCarteraFiltro, acredEstadoFiltro);
        return next;
      });

      setAcredPendingChanges({});
      toast.success(`Acreditaciones guardadas (${entries.length} cambio${entries.length !== 1 ? 's' : ''})`);
    } catch (e) {
      toast.error('Error al guardar acreditaciones');
      console.error(e);
    } finally {
      setAcredSaving(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Cuando se abre el modal, refetch la imagen si no existe en cache (por ejemplo tras un delete previo)
  useEffect(() => {
    if (isOpen && personal?.rut && !hookHasImage) {
      // Intentar obtener la imagen real desde el backend (si existe)
      try {
        refetchProfileImage();
      } catch (e) {
        // ignore
      }
    }
  }, [isOpen, personal?.rut]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0] && personal) {
      const file = event.target.files[0];
      try {
        await uploadImage(file);
        // Refetch image from the hook (uses inline image endpoint, not download)
        try { await refetchProfileImage(); } catch {};
        setImageError(false);
      } catch (error) {
        console.error("Error uploading image:", error);
        setImageError(true);
      }
    }
  };

  const handleRemoveImage = async () => {
    if (personal) {
      try {
        await deleteImage();
        // Refetch to update UI and show placeholder
        try { await refetchProfileImage(); } catch {};
        setImageError(true);
      } catch (error) {
        console.error("Error deleting image:", error);
      }
    }
  };

  // Obtener nombre del estado por id (usando datos de la API)
  const getEstadoNombreById = (id?: number) => {
    if (!id) return personal?.estado_nombre || 'Desconocido';
    const found = estadosData?.data?.find((e: any) => e.id === id);
    return found?.nombre || personal?.estado_nombre || 'Desconocido';
  };

  // Estado seleccionado en el formulario de edición (id → nombre normalizado).
  // Vacaciones y licencia médica se registran por RANGO en mantenimiento.personal_estados
  // (vía setEstadoRango), NO en columnas de personal_disponible.
  const selectedEstadoId = editData.estado_id ?? currentPersonal?.estado_id;
  const selectedEstadoNombre = useMemo(() => {
    const found = estadosData?.data?.find((e: any) => e.id === selectedEstadoId);
    return (found?.nombre || currentPersonal?.estado_nombre || '')
      .toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
  }, [estadosData, selectedEstadoId, currentPersonal?.estado_nombre]);
  const estadoRequiereRango = selectedEstadoNombre === 'vacaciones' || selectedEstadoNombre === 'licencia medica';

  // Normaliza un nombre de estado para comparar (sin acentos, minúsculas).
  const normEstado = (s?: string | null) =>
    (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

  // Eventos de estado (vacaciones/licencia) de esta persona, traídos de personal_estados.
  const [estadosEventos, setEstadosEventos] = useState<EstadoRangoEvento[]>([]);
  const prefilledEventoRef = useRef<string | null>(null);

  // Evento activo que corresponde al estado seleccionado: el de fecha "desde" más reciente.
  const eventoActivoEstado = useMemo(() => {
    if (!estadoRequiereRango) return null;
    // GET /personal-estados/persona/:rut devuelve el nombre del estado en `estado_nombre`,
    // no en `estado` — sin este fallback, esta búsqueda nunca encontraba el evento activo y
    // "editar" terminaba creando un registro nuevo en vez de actualizar el existente.
    const candidatos = estadosEventos.filter(e => normEstado((e as any).estado_nombre ?? e.estado) === selectedEstadoNombre);
    if (candidatos.length === 0) return null;
    return [...candidatos].sort((a, b) => (b.desde || '').localeCompare(a.desde || ''))[0];
  }, [estadosEventos, estadoRequiereRango, selectedEstadoNombre]);
  
  const handleDownloadDocument = async (documento: any) => {
    if (!documento.id) {
      console.error("ID de documento no válido");
      return;
    }
    // Bloquear descarga de documentos restringidos si el usuario no tiene permiso de visualización
    if (isRestrictedDoc(documento) && !(canViewRestricted || canAccessPrivileged)) {
      alert('No tienes permiso para descargar este documento.');
      return;
    }
    try {
      const preferredName = (documento.nombre_original || documento.nombre_archivo || documento.nombre_documento || '').toString();
      await downloadDocumentoMutation.mutateAsync({
        id: documento.id,
        preferredFilename: preferredName,
      });
    } catch (error: any) {
      console.error("Error al descargar el documento:", error);
      const status = error?.response?.status;
      const msg = status === 404
        ? 'El documento no está disponible para descargar (no se encontró en el servidor).'
        : (error?.response?.data?.message || error?.message || 'Error al descargar el documento');
      toast.error(msg);
    }
  };

  // Cargar documentos del personal desde el backend
  // Filtrar documentos por categorías
  // La API puede devolver varias formas:
  //  - { success: true, data: { documentos: [...] } }
  //  - { success: true, data: [...] } (array directo)
  //  - { success: true, data: { documentos_locales_split: { documentos: [...], cursos_certificaciones: [...] } } }
  // Normalizar a un array plano de documentos en `rawDocumentos` para evitar resultados vacíos.
  const rawDocumentos: any[] = (() => {
    // Normalize many possible shapes returned by the backend.
    // The hook may return: { success, data: [...] } OR { success, data: { documentos: [...] } }
    // or might already be the array. Add defensive logs to help debugging.
    if (isOpen) console.log('🔍 documentosData full:', documentosData);
    const d: any = documentosData?.data ?? documentosData;
    const externalDocs = prereqDetailOverride?.documentos_usados ?? prereqDetailInfo?.documentos_usados;
    const fallbackDocs = Array.isArray(externalDocs) ? externalDocs : [];
    if (!d) {
      // Fallback: if backend didn't return documentos but prereq detail contains documentos_usados,
      // expose those so the modal shows the matched documents computed by prerrequisitos API.
      return fallbackDocs;
    }

    // If backend returned a raw array, use it directly
    if (Array.isArray(d)) return d.length > 0 ? [...d, ...fallbackDocs] : fallbackDocs;

    // Prefer explicit top-level arrays returned by the backend when present and non-empty.
    // Backend may provide the documents under different keys (documentos, mis_documentos, items, rows)
    const preferredKeys = ['documentos', 'mis_documentos', 'items', 'rows', 'documentos_locales'];
    for (const k of preferredKeys) {
      if (Array.isArray(d[k]) && d[k].length > 0) return [...d[k], ...fallbackDocs];
    }

    // If the backend provides a split structure, combine only if it contains items.
    if (d.documentos_locales_split) {
      const split = d.documentos_locales_split;
      const docs = Array.isArray(split.documentos) ? split.documentos : [];
      const cursos = Array.isArray(split.cursos_certificaciones) ? split.cursos_certificaciones : [];
      if (docs.length > 0 || cursos.length > 0) return [...docs, ...cursos, ...fallbackDocs];
    }

    // Fallbacks: return empty arrays or any arrays even if empty to keep consistent behavior
    if (Array.isArray(d.documentos) && d.documentos.length > 0) return [...d.documentos, ...fallbackDocs];
    if (Array.isArray(d.documentos_locales) && d.documentos_locales.length > 0) return [...d.documentos_locales, ...fallbackDocs];

    return fallbackDocs;
  })();

  // Reuse the query above (`documentosData`) instead of creating a second query instance.
  // The first call to `useDocumentosByPersona` returns `isLoadingDocumentos` and `errorDocumentos`.

  // Dedupe: el backend a veces puede devolver entradas duplicadas (por ejemplo registro y copia local).
  // Usar el nombre de archivo normalizado como clave principal (elimina sufijos de timestamp, underscores, mayúsculas)
  const normalizeName = (name: string | undefined) => {
    if (!name) return '';
    // Lowercase
    let s = name.toString().toLowerCase();
    // Replace underscores and multiple separators with space
    s = s.replace(/[_\-]+/g, ' ');
    s = s.replace(/\s+/g, ' ').trim();
    // Remove trailing numeric timestamps (e.g. 1759500715991 or _1759500715991)
    s = s.replace(/(?:\s|_|-)*\d{9,}(?:\.\w+)?$/, '');
    // Trim again
    return s.trim();
  };

  // Normalizar un tipo para comparaciones: quitar acentos, convertir a minúsculas,
  // reemplazar guiones/underscores/espacios por espacio simple y trim.
  const normalizeTipoForCompare = (s: string | undefined) => {
    if (!s) return '';
    try {
      // eliminar diacríticos (acentos)
      let t = s.toString().toLowerCase().trim();
      t = t.normalize('NFD').replace(/\p{Diacritic}/gu, '');
      t = t.replace(/[_\-]+/g, ' ');
      t = t.replace(/\s+/g, ' ').trim();
      return t;
    } catch (e) {
      return (s || '').toString().toLowerCase().replace(/[_\-]+/g, ' ').trim();
    }
  };

  const formatSupervisorName = (sup?: string | null) => {
    if (!sup) return '';
    const raw = sup.toString().trim();
    if (!raw) return '';

    let surnames: string[] = [];
    let names: string[] = [];

    if (raw.includes(',')) {
      const parts = raw.split(',').map(p => p.trim());
      surnames = parts[0] ? parts[0].split(/\s+/) : [];
      names = parts[1] ? parts[1].split(/\s+/) : [];
    } else {
      const tokens = raw.split(/\s+/);
      if (tokens.length >= 4) {
        surnames = tokens.slice(0, 2);
        names = tokens.slice(2);
      } else if (tokens.length === 3) {
        // ambiguous: assume first two are surnames, last is name
        surnames = tokens.slice(0, 2);
        names = tokens.slice(2);
      } else {
        // fallback: return original
        return raw;
      }
    }

    const parts: string[] = [];
    if (names[0]) parts.push(names[0]);
    if (names[1]) parts.push(names[1]);
    if (surnames[0]) parts.push(surnames[0]);
    if (surnames[1]) parts.push(surnames[1]);

    return parts.join(' ').trim();
  };

  // IDs de documentos que ya cubren un requisito Belray. Se conservan por id para que
  // "Documentos Belray" y se excluyen de "Documentación Personal"/"Cursos y Certificados"
  // para no duplicarlos (algunos tipos Belray, ej. certificado_seguridad, también
  // califican como "curso/certificado" más abajo).
  const belrayDocumentIds = useMemo(() => new Set(
    requisitosBelray.filter((r: any) => r.cubierto && r.documentoId != null).map((r: any) => String(r.documentoId))
  ), [requisitosBelray]);

  const uniqueDocumentosMap = new Map<string, any>();
  for (const d of rawDocumentos) {
    const rawName = d?.nombre_original || d?.nombre_archivo || '';
    const normalized = normalizeName(rawName);
    const tipo = (d?.tipo_documento || '').toString().toLowerCase();
    // Un documento referenciado por un requisito Belray nunca se colapsa contra otro:
    // si dos subidas comparten nombre y tipo (típico en IRL/ODI, que son formularios
    // genéricos), la deduplicación se quedaba con la copia equivocada y la tarjeta
    // Belray dejaba de encontrar su documento — quedando sin botones de acción.
    const esReferenciadoBelray = d?.id != null && belrayDocumentIds.has(String(d.id));
    const key = esReferenciadoBelray
      ? `id::${d.id}`
      : (normalized ? `${normalized}::${tipo}` : (d?.id ? `id::${d.id}` : JSON.stringify(d)));
    if (!uniqueDocumentosMap.has(key)) uniqueDocumentosMap.set(key, d);
  }
  // Los documentos dados de baja (activo === false) no se muestran a ningún usuario.
  const todosDocumentos = Array.from(uniqueDocumentosMap.values()).filter((d: any) => d?.activo !== false);

  // Documentos de cursos y certificados
  // Incluye: curso, certificacion, certificación y tipos legacy
  const courseDocuments = todosDocumentos.filter((doc: any) => {
    const tipo = doc.tipo_documento?.toLowerCase() || '';
    return tipo === 'curso' ||
           tipo === 'certificacion' ||
           tipo === 'certificación' ||
           tipo === 'curso/certificacion' ||
           tipo === 'curso/certificación' ||
           tipo === 'certificado_curso' ||
           tipo === 'diploma' ||
           tipo === 'certificado_seguridad' ||
           tipo === 'certificado_vencimiento';
  });

  // Documentos personales (carnet, exámenes, licencias, etc.)
  const personalDocuments = todosDocumentos.filter((doc: any) => {
    const tipo = doc.tipo_documento?.toLowerCase() || '';
    // Si no es un curso/certificación, entonces es documento personal
    const esCurso = tipo === 'curso' ||
                    tipo === 'certificacion' ||
                    tipo === 'certificación' ||
                    tipo === 'curso/certificacion' ||
                    tipo === 'curso/certificación' ||
                    tipo === 'certificado_curso' ||
                    tipo === 'diploma' ||
                    tipo === 'certificado_seguridad' ||
                    tipo === 'certificado_vencimiento';
    return !esCurso;
  });

  // Vista filtrada por área responsable (RRHH/Prevención/N/A) para identificar rápido
  // qué documentos le corresponde revisar a cada área. `personalDocuments` (sin filtrar)
  // se sigue usando para lógica de negocio (ej. cascada de borrado por curso).
  const personalDocumentsFiltered = useMemo(() => {
    let docs = personalDocuments;

    // Filtro por área responsable
    if (areaFilterDocs !== 'todas') {
      docs = docs.filter((doc: any) => {
        const area = getArea(doc.tipo_documento);
        return areaFilterDocs === 'sin_area' ? !area : area === areaFilterDocs;
      });
    }

    // Buscador de texto (nombre, tipo o descripción)
    const q = docSearchTerm.trim().toLowerCase();
    if (q) {
      docs = docs.filter((doc: any) =>
        (doc.nombre_documento || doc.nombre_original || doc.nombre || '').toLowerCase().includes(q) ||
        (doc.tipo_documento || '').toLowerCase().includes(q) ||
        (doc.descripcion || '').toLowerCase().includes(q)
      );
    }

    return docs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalDocuments, areaFilterDocs, getArea, docSearchTerm]);

  // Ensure prereq page resets when right-column content or missing count changes
  useEffect(() => {
    const rightCount = (Array.isArray(courseDocuments) ? courseDocuments.length : 0) + (Array.isArray(personalDocuments) ? personalDocuments.length : 0);
    let pageSizeLocal = 10;
    if (rightCount > 20) pageSizeLocal = 4;
    else if (rightCount > 8) pageSizeLocal = 6;
    const totalMissing = (prereqDetailOverride?.documentos_faltantes?.length) ?? (prereqDetailInfo?.documentos_faltantes?.length) ?? 0;
    const totalPagesLocal = Math.max(1, Math.ceil(totalMissing / pageSizeLocal));
    if (prereqPage > totalPagesLocal) setPrereqPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseDocuments, personalDocuments, prereqDetailOverride, prereqDetailInfo]);

  // documentos_disponibles (calculado por matchForCliente para el cliente activo) trae vencido
  // + fecha_limite_vigencia; se indexa por id para las listas de documentos crudos
  // (Cursos/Personales). Desde 2026-10-08 el dias_duracion del requisito ya no vence documentos:
  // solo cuenta la fecha_vencimiento propia.
  const vencimientoPorClienteActivoMap = useMemo(() => {
    const data = prereqDetailOverride || prereqDetailInfo;
    const disponibles: any[] = Array.isArray(data?.documentos_disponibles) ? data.documentos_disponibles : [];
    const map = new Map<number, any>();
    for (const d of disponibles) if (d?.id != null) map.set(Number(d.id), d);
    return map;
  }, [prereqDetailOverride, prereqDetailInfo]);

  const esDocumentoVencido = (documento: any): boolean => {
    if (!documento) return false;
    const porCliente = vencimientoPorClienteActivoMap.get(Number(documento.id));
    if (porCliente) return porCliente.vencido === true;
    // Fallback a fecha_vencimiento explícita cuando no hay cliente activo (o el documento no
    // aparece en documentos_disponibles, ej. tipos que ningún requisito del cliente evalúa).
    return documento.fecha_vencimiento ? new Date(documento.fecha_vencimiento) < new Date() : false;
  };


  // Texto explicando POR QUÉ está vencido.
  const explicarVencido = (documento: any): string => {
    const info = documento ? vencimientoPorClienteActivoMap.get(Number(documento.id)) : null;
    if (info?.vencido) {
      if (info.vencimiento_origen === 'fecha_explicita' && info.fecha_limite_vigencia) {
        return `El documento venció el ${formatDate(info.fecha_limite_vigencia)} (fecha de vencimiento propia del archivo).`;
      }
    }
    if (documento?.fecha_vencimiento && new Date(documento.fecha_vencimiento) < new Date()) {
      return `El documento venció el ${formatDate(documento.fecha_vencimiento)}.`;
    }
    return 'El documento venció.';
  };

  // Versión corta para mostrar SIEMPRE visible junto al badge (no solo al pasar el mouse).
  const explicarVencidoCorto = (documento: any): string => {
    const info = documento ? vencimientoPorClienteActivoMap.get(Number(documento.id)) : null;
    if (info?.vencido && info.vencimiento_origen === 'fecha_explicita' && info.fecha_limite_vigencia) {
      return `venció ${formatDate(info.fecha_limite_vigencia)}`;
    }
    if (documento?.fecha_vencimiento) return `venció ${formatDate(documento.fecha_vencimiento)}`;
    return '';
  };

  const handleEditDocumentModalClose = () => {
    setShowEditDocumentModal(false);
    setEditingDocumento(null);
    setEditingDocumentoContext(null);
  };

  const handleEditDocumentSuccess = () => {
    refetchDocumentos();
    avisarCambioCoberturaPersona(personal?.rut);
    // Editar un documento puede cambiar su id (al reemplazar el archivo se sube uno nuevo
    // y se borra el viejo), así que hay que releer la cobertura Belray y la del cliente.
    refreshBelrayPrereqs();
    (async () => {
      if (!clienteActivoId || !personal?.rut) return;
      try {
        const fresh = await fetchMergedPrereqsCached(Number(clienteActivoId), personal.rut, { fresh: true });
        setPrereqDetailOverride(applyLocalOverrides(fresh));
        window.dispatchEvent(new CustomEvent('prereqDetailUpdated', { detail: { rut: personal.rut, clienteId: clienteActivoId, detail: applyLocalOverrides(fresh) } }));
      } catch (e) {
        console.warn('No se pudo refrescar requisitos tras editar/reemplazar documento:', e);
      }
    })();
    setShowEditDocumentModal(false);
    setEditingDocumento(null);
    setEditingDocumentoContext(null);
  };

  const handleCourseDocumentModalClose = () => {
    setShowCourseDocumentModal(false);
    setSelectedCurso(null);
  };

  // Inicializar datos de edición cuando se abre la modal
  useEffect(() => {
    if (currentPersonal && isOpen) {
      setEditData({
        nombre: currentPersonal.nombre || '',
        apellido: currentPersonal.apellido || '',
        cargo: currentPersonal.cargo,
        profesion: currentPersonal.profesion || '',
        area: currentPersonal.area || '',
        supervisor: currentPersonal.supervisor || '',
        telefono: (currentPersonal.telefono as any) || '',
        email: currentPersonal.email || '',
        contacto: currentPersonal.contacto || undefined,
        contacto_emergencia: currentPersonal.contacto_emergencia || undefined,
        sexo: currentPersonal.sexo,
        licencia_conducir: currentPersonal.licencia_conducir,
        talla_zapatos: currentPersonal.talla_zapatos,
        talla_pantalon: currentPersonal.talla_pantalon,
        talla_poleras: currentPersonal.talla_poleras,
        ubicacion: {
          region: currentPersonal.ubicacion?.region || '',
          ciudad: currentPersonal.ubicacion?.ciudad || '',
          comuna: currentPersonal.ubicacion?.comuna || '',
          direccion: currentPersonal.ubicacion?.direccion || ''
        },
        activo: currentPersonal.activo,
        estado_id: currentPersonal.estado_id,
        comentario_estado: currentPersonal.comentario_estado,
        fecha_nacimiento: currentPersonal.fecha_nacimiento ? currentPersonal.fecha_nacimiento.split('T')[0] : '',
        edad: currentPersonal.fecha_nacimiento ? getAge(currentPersonal.fecha_nacimiento).toString() : '',
      });
    }
  }, [currentPersonal, isOpen]);

  // Cargar los eventos de estado (vacaciones/licencia) de la persona desde personal_estados.
  // Endpoint exacto por rut (no /overlap, que es por semana y para todos).
  useEffect(() => {
    if (!isOpen || !personal?.rut) {
      setEstadosEventos([]);
      prefilledEventoRef.current = null;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const eventos = await getEstadosPersona(personal.rut);
        if (!cancelled) setEstadosEventos(eventos);
      } catch (e) {
        console.warn('No se pudieron cargar los estados por rango de la persona:', e);
        if (!cancelled) setEstadosEventos([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, personal?.rut]);

  // Prefill de los inputs Desde/Hasta con el evento activo, al editar un estado con rango.
  // Solo prellena una vez por evento; no pisa lo que el usuario empiece a escribir.
  useEffect(() => {
    if (!isEditing || !estadoRequiereRango || !eventoActivoEstado) return;
    const marker = `${eventoActivoEstado.id}:${selectedEstadoNombre}`;
    if (prefilledEventoRef.current === marker) return;
    prefilledEventoRef.current = marker;
    setEditData(prev => ({
      ...prev,
      estado_temporal_desde: (eventoActivoEstado.desde || '').slice(0, 10),
      estado_temporal_hasta: (eventoActivoEstado.hasta || '').slice(0, 10),
    }));
  }, [isEditing, estadoRequiereRango, eventoActivoEstado, selectedEstadoNombre]);

  if (!isOpen || !currentPersonal) {
    return null;
  }

  const handleInputChange = (field: string, value: any) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (editData.nombre !== undefined && !editData.nombre?.trim()) newErrors.nombre = 'El nombre es requerido';
    if (editData.apellido !== undefined && !editData.apellido?.trim()) newErrors.apellido = 'El apellido es requerido';
    if (editData.cargo !== undefined && !editData.cargo?.trim()) newErrors.cargo = 'El cargo es requerido';
    if (editData.licencia_conducir !== undefined && !editData.licencia_conducir?.trim()) newErrors.licencia_conducir = 'La licencia es requerida';
    // zona_geografica removed from UI per request — skip validation here
    // Validación de contacto
    const emailValue = (editData as any).email || currentPersonal.email || '';
    const telefonoValue = (editData as any).telefono || currentPersonal.telefono || '';
    const contactoEmerg = (editData as any).contacto_emergencia || currentPersonal.contacto_emergencia || {};

    const isValidEmail = (value: string) => {
      if (!value) return true; // vacío permitido, se maneja como 'sin email'
      return /^\S+@\S+\.\S+$/.test(value.trim());
    };

    const isValidPhone = (value: string) => {
      if (!value) return true; // vacío permitido
      const digits = (value || '').replace(/\D/g, '');
      if (digits.length < 6) return false;
      return /^[+]?[-()\s\d]+$/.test(value.trim());
    };

    if (!isValidPhone(telefonoValue)) newErrors.telefono = 'Teléfono inválido (mínimo 6 dígitos)';
    if (!isValidEmail(emailValue)) newErrors.email = 'Email con formato inválido';

    // Si se provee algún dato de contacto de emergencia, validar campos mínimos
    const anyEmerg = Boolean(contactoEmerg && (contactoEmerg.nombre || contactoEmerg.telefono || contactoEmerg.email || contactoEmerg.relacion));
    if (anyEmerg) {
      if (!contactoEmerg.nombre || !String(contactoEmerg.nombre).trim()) newErrors['contacto_emergencia.nombre'] = 'Nombre del contacto de emergencia es requerido';
      if (!contactoEmerg.telefono || !String(contactoEmerg.telefono).trim()) newErrors['contacto_emergencia.telefono'] = 'Teléfono del contacto de emergencia es requerido';
      if (contactoEmerg.telefono && !isValidPhone(String(contactoEmerg.telefono))) newErrors['contacto_emergencia.telefono'] = 'Teléfono de emergencia inválido';
      if (contactoEmerg.email && !isValidEmail(String(contactoEmerg.email))) newErrors['contacto_emergencia.email'] = 'Email de emergencia con formato inválido';
    }
    if (editData.comentario_estado && editData.comentario_estado.length > 1000) newErrors.comentario_estado = 'El comentario no puede exceder 1000 caracteres';
    // Si el estado requiere rango (vacaciones/licencia médica), validar las fechas.
    // Solo se exige al CAMBIAR a ese estado (no si ya lo tenía y edita otros campos).
    if (estadoRequiereRango && editData.estado_id !== undefined && editData.estado_id !== currentPersonal.estado_id) {
      const desde = (editData.estado_temporal_desde ?? '').slice(0, 10);
      const hasta = (editData.estado_temporal_hasta ?? '').slice(0, 10);
      if (!desde) {
        newErrors.estado_temporal_desde = 'Indica la fecha "Desde" para el estado seleccionado';
      }
      if (desde && hasta && hasta < desde) {
        newErrors.estado_temporal_hasta = 'La fecha "Hasta" no puede ser anterior a "Desde"';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!currentPersonal) return;
    if (!validateForm()) return;
    try {
      console.log('💾 [PersonalDetailModal] Guardando cambios...', editData);
      const promises = [] as any[];

      // Detectar cambios reales comparando editData con currentPersonal
      const changedName = (editData.nombre !== undefined && editData.nombre !== currentPersonal.nombre) ||
        (editData.apellido !== undefined && editData.apellido !== currentPersonal.apellido);

      const changedFields: any = {};
      const addIfChanged = (key: string, mappedKey?: string) => {
        const k = mappedKey || key;
        const newVal = (editData as any)[key];
        const oldVal = (currentPersonal as any)[k];
        if (newVal !== undefined && String(newVal) !== String(oldVal)) changedFields[k] = newVal;
      };

      addIfChanged('cargo');
      addIfChanged('profesion');
      addIfChanged('area');
      addIfChanged('supervisor');
      addIfChanged('licencia_conducir');
      addIfChanged('estado_id');
      addIfChanged('talla_zapatos', 'talla_zapato');
      addIfChanged('talla_pantalon');
      addIfChanged('talla_poleras', 'talla_ropa');
      addIfChanged('sexo');
      addIfChanged('telefono');
      addIfChanged('email', 'correo_electronico');
      addIfChanged('comentario_estado');

      // Fecha de nacimiento: normalizar a YYYY-MM-DD antes de comparar
      const normalizeDate = (v: any) => {
        if (!v) return '';
        if (typeof v === 'string' && v.indexOf('T') > 0) return v.split('T')[0];
        if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
        try {
          const d = parseDateStringToLocal(String(v));
          if (isNaN(d.getTime())) return '';
          return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        } catch { return '' }
      };
      const newFecha = normalizeDate((editData as any).fecha_nacimiento ?? '');
      const oldFecha = normalizeDate(currentPersonal.fecha_nacimiento ?? '');
      if (newFecha && newFecha !== oldFecha) changedFields.fecha_nacimiento = newFecha;

      // Ubicación: region/ciudad/comuna/direccion
      const newUbic = (editData as any).ubicacion || {};
      const oldUbic = currentPersonal.ubicacion || {};
      ['region','ciudad','comuna','direccion'].forEach((f) => {
        const newVal = (newUbic as any)[f];
        const oldVal = (oldUbic as any)[f];
        if (newVal !== undefined && String(newVal || '') !== String(oldVal || '')) {
          changedFields[f] = newVal;
        }
      });

      // Contacto de emergencia
      const newContact = (editData as any).contacto_emergencia || undefined;
      const oldContact = currentPersonal.contacto_emergencia || undefined;
      if (newContact !== undefined && JSON.stringify(newContact) !== JSON.stringify(oldContact)) changedFields.contacto_emergencia = newContact;

      // Estado por rango (vacaciones/licencia médica) → mantenimiento.personal_estados.
      // Solo se registra si hay "desde" y el rango realmente cambió respecto al evento
      // activo prellenado (evita re-guardar / reload cuando no hubo edición real).
      const desdeInput = (editData.estado_temporal_desde ?? '').slice(0, 10);
      const hastaInput = (editData.estado_temporal_hasta ?? '').slice(0, 10);
      const rangoCambiado = !eventoActivoEstado
        || desdeInput !== (eventoActivoEstado.desde || '').slice(0, 10)
        || hastaInput !== (eventoActivoEstado.hasta || '').slice(0, 10);
      const debeGuardarRango = estadoRequiereRango && !!desdeInput.trim() && rangoCambiado;

      // ── Limpieza de vacaciones / licencia al dejar a la persona en "Activo" ──
      // El estado "vacaciones/licencia" vive como RANGO en personal_estados, no en
      // personal_disponible (cuyo estado suele quedar en Activo). Por eso, al guardar
      // con estado Activo se eliminan los períodos ya terminados (lingering) para que
      // dejen de aparecer en la planificación; si además se cambió explícitamente a
      // Activo, también se cierran los vigentes. Los planificados a futuro se conservan.
      const ahoraLimpieza = new Date();
      const hoyYMD = `${ahoraLimpieza.getFullYear()}-${String(ahoraLimpieza.getMonth() + 1).padStart(2, '0')}-${String(ahoraLimpieza.getDate()).padStart(2, '0')}`;
      const nuevoEstadoId = editData.estado_id ?? currentPersonal.estado_id;
      const nuevoEstadoNombre = normEstado(estadosData?.data?.find((e: any) => e.id === nuevoEstadoId)?.nombre || currentPersonal.estado_nombre);
      const cambioExplicitoActivo =
        editData.estado_id !== undefined && editData.estado_id !== currentPersonal.estado_id;
      const eventosLeaveALimpiar = nuevoEstadoNombre !== 'activo' ? [] : estadosEventos.filter((ev) => {
        const nombre = normEstado(ev.estado);
        if ((nombre !== 'vacaciones' && nombre !== 'licencia medica') || ev.id == null) return false;
        const desde = (ev.desde || '').slice(0, 10);
        const hasta = (ev.hasta || '').slice(0, 10);
        if (desde > hoyYMD) return false;               // planificada a futuro → conservar
        if (hasta && hasta < hoyYMD) return true;        // ya terminada → limpiar siempre
        return cambioExplicitoActivo;                    // vigente/abierta → solo si se pasó a Activo
      });
      const debeLimpiarLeaves = eventosLeaveALimpiar.length > 0;

      // Si no hubo cambios detectados, cerrar edición sin llamar al backend ni invalidar caché
      if (!changedName && Object.keys(changedFields).length === 0 && !debeGuardarRango && !debeLimpiarLeaves) {
        console.log('🔍 [PersonalDetailModal] No se detectaron cambios — cerrando editor sin actualizar');
        // Resetear campos de edición a los valores actuales por seguridad
        handleCancel();
        return;
      }

      // Si cambiaron nombre/apellido, llamar al endpoint específico
      if (changedName) {
        console.log('📝 [PersonalDetailModal] Nombre/apellido modificados — llamando /api/nombres');
        promises.push(
          updateNombreMutation.mutateAsync({
            rut: currentPersonal.rut,
            nombre: editData.nombre ?? currentPersonal.nombre,
            apellido: editData.apellido ?? currentPersonal.apellido,
            sexo: editData.sexo ?? currentPersonal.sexo,
            fecha_nacimiento: currentPersonal.fecha_nacimiento,
            licencia_conducir: currentPersonal.licencia_conducir,
          })
        );
      }

      // Preparar payload para otros campos - siempre incluir campos obligatorios del backend
      if (Object.keys(changedFields).length > 0) {
        console.log('🔄 [PersonalDetailModal] Actualizando otros campos:', changedFields);
        const nombreCompleto = standardizeName(`${editData.nombre ?? currentPersonal.nombre ?? ''} ${editData.apellido ?? currentPersonal.apellido ?? ''}`.trim());
        
        // Construir payload completo con campos obligatorios + cambios
        const updateData: any = {
          // Campos obligatorios del backend (siempre incluir)
          sexo: editData.sexo ?? currentPersonal.sexo,
          fecha_nacimiento: editData.fecha_nacimiento ?? currentPersonal.fecha_nacimiento,
          licencia_conducir: editData.licencia_conducir ?? currentPersonal.licencia_conducir,
          cargo: editData.cargo ?? currentPersonal.cargo,
          estado_id: editData.estado_id ?? currentPersonal.estado_id,
          // Nombre completo
          nombres: nombreCompleto,
          // Profesión, Área y Supervisor
          ...(editData.profesion !== undefined || currentPersonal.profesion ? { profesion: editData.profesion ?? currentPersonal.profesion } : {}),
          ...(editData.area !== undefined || currentPersonal.area ? { area: editData.area ?? currentPersonal.area } : {}),
          ...(editData.supervisor !== undefined || currentPersonal.supervisor ? { supervisor: editData.supervisor ?? currentPersonal.supervisor } : {}),
          // Campos opcionales si están disponibles
          ...(editData.telefono !== undefined || currentPersonal.telefono ? { telefono: editData.telefono ?? currentPersonal.telefono } : {}),
          ...(editData.email !== undefined || currentPersonal.email ? { correo_electronico: editData.email ?? currentPersonal.email } : {}),
          // Ubicación
          ...(editData.ubicacion?.region || currentPersonal.ubicacion?.region ? { region: editData.ubicacion?.region ?? currentPersonal.ubicacion?.region } : {}),
          ...(editData.ubicacion?.comuna || currentPersonal.ubicacion?.comuna ? { comuna: editData.ubicacion?.comuna ?? currentPersonal.ubicacion?.comuna } : {}),
          ...(editData.ubicacion?.ciudad || currentPersonal.ubicacion?.ciudad ? { ciudad: editData.ubicacion?.ciudad ?? currentPersonal.ubicacion?.ciudad } : {}),
          ...(editData.ubicacion?.direccion || currentPersonal.ubicacion?.direccion ? { direccion: editData.ubicacion?.direccion ?? currentPersonal.ubicacion?.direccion } : {}),
          // Tallas
          ...(editData.talla_poleras !== undefined || currentPersonal.talla_poleras ? { talla_ropa: editData.talla_poleras ?? currentPersonal.talla_poleras } : {}),
          ...(editData.talla_pantalon !== undefined || currentPersonal.talla_pantalon ? { talla_pantalon: editData.talla_pantalon ?? currentPersonal.talla_pantalon } : {}),
          ...(editData.talla_zapatos !== undefined || currentPersonal.talla_zapatos ? { talla_zapato: editData.talla_zapatos ?? currentPersonal.talla_zapatos } : {}),
          // Incluir campos modificados
          ...changedFields,
        };

        console.log('📤 [PersonalDetailModal] Payload completo a enviar:', updateData);
        promises.push(updateMutation.mutateAsync({ id: currentPersonal.rut, data: updateData as any }));
      }

      // Registrar el rango de vacaciones/licencia médica en personal_estados (fuente de
      // verdad compartida con el modal de planificación y leída por el export semanal).
      // Si ya existe un evento activo de ese estado → PUT (editar); si no → POST (crear).
      // Evita acumular filas duplicadas por el mismo período.
      if (debeGuardarRango) {
        const desde = (editData.estado_temporal_desde ?? '').slice(0, 10);
        const hasta = (editData.estado_temporal_hasta ?? '').slice(0, 10) || null;
        if (eventoActivoEstado?.id) {
          console.log('🗓️ [PersonalDetailModal] Editando estado por rango:', { id: eventoActivoEstado.id, estado: selectedEstadoNombre, desde, hasta });
          promises.push(updateEstadoRango(eventoActivoEstado.id, { estado: selectedEstadoNombre, desde, hasta }));
        } else {
          console.log('🗓️ [PersonalDetailModal] Registrando estado por rango:', { rut: currentPersonal.rut, estado: selectedEstadoNombre, desde, hasta });
          promises.push(setEstadoRango({ rut: currentPersonal.rut, estado: selectedEstadoNombre, desde, hasta }));
        }
      }

      // Eliminar los períodos de vacaciones / licencia detectados arriba (persona en Activo).
      for (const ev of eventosLeaveALimpiar) {
        console.log('🗑️ [PersonalDetailModal] Eliminando estado por rango (persona Activo):', ev);
        promises.push(deleteEstadoRango(ev.id));
      }

      await Promise.all(promises);
      console.log('✅ [PersonalDetailModal] Actualizaciones completadas');
      
      const newEstadoId = editData.estado_id !== undefined ? editData.estado_id : currentPersonal.estado_id;
      if (newEstadoId !== undefined) setCurrentEstadoId(newEstadoId);
      
      // Invalidar todas las queries de personal para forzar refetch
      console.log('🔄 Invalidando queries de personal para refrescar datos...');
      await queryClient.invalidateQueries({ queryKey: ['personal'] });
      await queryClient.invalidateQueries({ queryKey: ['personal', 'detail', currentPersonal.rut] });
      await queryClient.invalidateQueries({ queryKey: ['personal', 'list'] });
      
      setIsEditing(false);
      console.log('✅ Datos actualizados - recargando página para refrescar vista...');
      
      // Guardar el RUT en localStorage para reabrir el modal después del reload
      localStorage.setItem('reopenPersonalDetailRUT', currentPersonal.rut);
      
      // Mostrar toast de éxito antes de recargar
      toast.success('Cambios guardados. Recargando página...');
      
      // Recargar la página después de un breve delay para que el usuario vea el toast
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (error: any) {
      console.error('Error al actualizar:', error);
      setErrors({ general: 'Error al actualizar el personal. Verifique los datos ingresados.' });
    }
  };

  const handleCancel = () => {
    if (!currentPersonal) return;
    setEditData({
      nombre: currentPersonal.nombre || '',
      apellido: currentPersonal.apellido || '',
      cargo: currentPersonal.cargo,
      sexo: currentPersonal.sexo,
      licencia_conducir: currentPersonal.licencia_conducir,
      talla_zapatos: currentPersonal.talla_zapatos,
      talla_pantalon: currentPersonal.talla_pantalon,
      talla_poleras: currentPersonal.talla_poleras,
      ubicacion: {
        region: currentPersonal.ubicacion?.region || '',
        ciudad: currentPersonal.ubicacion?.ciudad || '',
        comuna: currentPersonal.ubicacion?.comuna || '',
        direccion: currentPersonal.ubicacion?.direccion || ''
      },
      activo: currentPersonal.activo,
      estado_id: currentPersonal.estado_id,
      // El rango (vacaciones/licencia) vive en personal_estados, no en personal_disponible:
      // los inputs se prellenan desde el evento activo al re-entrar a edición.
      estado_temporal_desde: '',
      estado_temporal_hasta: '',
      comentario_estado: currentPersonal.comentario_estado,
    });
    prefilledEventoRef.current = null; // permitir re-prefill al volver a editar
    setIsEditing(false);
    setErrors({});
  };

  const handleAddCurso = () => { setEditingCurso(null); setShowCursoModal(true); };
  const handleEditCurso = (curso: any) => { setEditingCurso(curso); setShowCursoModal(true); };
  const handleDeleteCurso = async (curso: any) => {
    if (window.confirm(`¿Está seguro que desea eliminar el curso "${curso.nombre_curso}"?`)) {
      try { await deleteCursoMutation.mutateAsync(curso.id); refetchCursos(); } catch {}
    }
  };

  // Funciones para manejar documentos de cursos
  // `contexto` conserva el cliente/prerrequisito desde el que se abrió la edición (ej.
  // la tarjeta Belray) para que un reemplazo de archivo no pierda la cobertura.
  const handleEditDocumento = (documento: any, contexto?: { clienteId?: number | null; prereqId?: number | string | null }) => {
    setEditingDocumento(documento);
    setEditingDocumentoContext(contexto ? { clienteId: contexto.clienteId ?? null, prereqId: contexto.prereqId ?? null } : null);
    setShowEditDocumentModal(true);
  };

  const handlePreviewDocumento = (documento: any) => {
    setPreviewDocumento(documento);
    setShowPreviewModal(true);
  };

  const toggleSelectDocument = (docId: number | string) => {
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };

  const clearSelected = () => setSelectedDocs(new Set());

  const loadHistorialDocumento = async (docId: number | string) => {
    const key = String(docId);
    if (historialByDoc[key]) return; // ya cargado o cargando
    setHistorialByDoc(prev => ({ ...prev, [key]: { loading: true, items: [] } }));
    try {
      const res = await apiService.getHistorialDocumento(Number(docId));
      const items = Array.isArray((res as any)?.data) ? (res as any).data : (Array.isArray(res) ? res : []);
      setHistorialByDoc(prev => ({ ...prev, [key]: { loading: false, items } }));
    } catch (e: any) {
      setHistorialByDoc(prev => ({ ...prev, [key]: { loading: false, items: [], error: 'No se pudo cargar el historial' } }));
    }
  };

  const toggleExpandDocument = (docId: number | string) => {
    setExpandedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };

  // Abre la ventana de versiones anteriores de un documento. Es el mismo camino para todas
  // las tarjetas; la carga del historial es bajo demanda y queda cacheada en historialByDoc.
  const abrirHistorialDocumento = (documento: any) => {
    setHistorialDoc(documento);
    loadHistorialDocumento(documento.id);
  };

  
  // Único camino de refresco tras un borrado: documentos + requisitos del cliente activo +
  // BelRay (panel derecho) + onUpdate del padre. Antes cada botón de borrar disparaba su
  // propia combinación distinta de estos pasos (algunos ninguno), dejando la ficha
  // desincronizada — el cambio no se veía hasta recargar la página a mano.
  const refrescarTrasBorrarDocumento = async () => {
    await refetchDocumentos();
    avisarCambioCoberturaPersona(personal?.rut);
    if (clienteActivoId && personal?.rut) {
      try {
        const fresh = await fetchMergedPrereqsCached(Number(clienteActivoId), personal.rut, { fresh: true });
        const overridden = applyLocalOverrides(fresh);
        setPrereqDetailOverride(overridden);
        window.dispatchEvent(new CustomEvent('prereqDetailUpdated', { detail: { rut: personal.rut, clienteId: clienteActivoId, detail: overridden } }));
      } catch (err) {
        console.warn('Error refrescando requisitos tras eliminar documento:', err);
      }
    }
    refreshBelrayPrereqs();
    try {
      if (typeof onUpdate === 'function') await onUpdate();
    } catch (err) {
      console.warn('Error llamando onUpdate tras eliminar documento:', err);
    }
  };

  const handleDeleteDocumento = async (documento: any) => {
    // Validar que el documento tenga un ID válido
    if (!documento?.id || documento.id === undefined || documento.id === null) {
      console.error('❌ No se puede eliminar: documento sin ID', documento);
      toast.error('Este documento no tiene un ID válido y no puede ser eliminado.');
      return;
    }

    const docId = documento.id;
    setDeletingDocId(docId);
    try {
      // toast.promise avisa DE INMEDIATO que el click se registró ("Eliminando...") y
      // actualiza solo al resultado — antes no había ningún indicio hasta que el refetch
      // (más lento) terminaba y la fila desaparecía sola.
      await toast.promise(
        deleteDocumentoWithClonesMutation.mutateAsync({ id: docId, eliminarClones: false }),
        {
          pending: 'Eliminando documento...',
          success: 'Documento eliminado',
          error: {
            render({ data }: any) {
              return data?.hasClones
                ? 'Este documento tiene clones asociados: hay que confirmar cómo eliminarlos.'
                : motivoErrorBorrado(data);
            }
          }
        }
      );
      await refrescarTrasBorrarDocumento();
    } catch (error: any) {
      console.error("Error al eliminar:", error);
      // Si tiene clones, mostrar confirmación especial (el toast de error ya avisó)
      if (error?.hasClones) {
        setDeleteConfirmation({
          show: true,
          docId,
          hasClones: true,
          totalClones: error.totalClones || 0,
          clones: error.clones || []
        });
      }
    } finally {
      setDeletingDocId(null);
    }
  };

  // Confirmar eliminación con clones
  const handleConfirmDeleteWithClones = async () => {
    if (!deleteConfirmation.docId) return;

    const docId = deleteConfirmation.docId;
    setDeletingDocId(docId);
    try {
      await toast.promise(
        deleteDocumentoWithClonesMutation.mutateAsync({ id: docId, eliminarClones: true }),
        {
          pending: 'Eliminando documento y sus clones...',
          success: 'Documento y clones eliminados',
          error: 'Error al eliminar el documento y sus clones. Intenta nuevamente.'
        }
      );
      setDeleteConfirmation({ show: false, docId: null, hasClones: false, totalClones: 0, clones: [] });
      await refrescarTrasBorrarDocumento();
    } catch (error) {
      console.error("Error al eliminar documento con clones:", error);
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleBulkDelete = async (ids: Array<number | string>) => {
    if (!ids || ids.length === 0) return;
    
    // Filtrar documentos que tienen ID válido
    const validDocs = ids
      .map(id => todosDocumentos.find((d: any) => String(d.id) === String(id)))
      .filter(doc => doc?.id !== undefined && doc?.id !== null);
    
    const invalidCount = ids.length - validDocs.length;
    if (invalidCount > 0) {
      console.warn(`⚠️ ${invalidCount} documento(s) no tienen ID válido y serán omitidos`);
    }
    
    if (validDocs.length === 0) {
      alert('Error: Ninguno de los documentos seleccionados tiene un ID válido para eliminar.');
      return;
    }
    
    if (!window.confirm(`¿Seguro que quieres eliminar ${validDocs.length} documento(s)? Esta acción no se puede deshacer.`)) return;
    try {
      await toast.promise(
        Promise.all(validDocs.map((doc) => deleteDocumentoWithClonesMutation.mutateAsync({ id: doc.id, eliminarClones: false }))),
        {
          pending: `Eliminando ${validDocs.length} documento(s)...`,
          success: `${validDocs.length} documento(s) eliminados`,
          error: { render({ data }: any) { return motivoErrorBorrado(data, 'Error al eliminar los documentos. Intenta nuevamente.'); } }
        }
      );
      clearSelected();
      await refrescarTrasBorrarDocumento();
    } catch (error) {
      console.error('Error en eliminación masiva:', error);
    }
  };

  const handleDeleteAllByType = async (tipo: string) => {
    const matches = todosDocumentos.filter((d: any) => d.tipo_documento === tipo);
    const validMatches = matches.filter((d: any) => d?.id !== undefined && d?.id !== null);
    
    if (validMatches.length === 0) {
      alert(`No hay documentos válidos de tipo "${tipo}" para eliminar.`);
      return;
    }
    
    const invalidCount = matches.length - validMatches.length;
    if (invalidCount > 0) {
      console.warn(`⚠️ ${invalidCount} documento(s) de tipo "${tipo}" no tienen ID válido y serán omitidos`);
    }
    
    if (window.confirm(`¿Seguro que quieres eliminar ${validMatches.length} documentos de tipo "${tipo}"?`)) {
      const deletions = validMatches.map((m: any) => deleteDocumentoWithClonesMutation.mutateAsync({ id: m.id, eliminarClones: false }));
      try {
        await toast.promise(Promise.all(deletions), {
          pending: `Eliminando ${validMatches.length} documento(s) de tipo "${tipo}"...`,
          success: `${validMatches.length} documento(s) eliminados`,
          error: { render({ data }: any) { return motivoErrorBorrado(data, 'Error al eliminar los documentos. Intenta nuevamente.'); } }
        });
        await refrescarTrasBorrarDocumento();
      } catch (error) {
        console.error("Error en eliminación masiva:", error);
      }
    }
  };

  const handleCursoModalClose = () => { setShowCursoModal(false); setEditingCurso(null); };
  const handleCursoSuccess = () => { refetchCursos(); };
  const handleAddCourseDocument = (curso: any) => { setSelectedCurso(curso); setShowCourseDocumentModal(true); };
  const handleCourseDocumentSuccess = () => { refetchDocumentos(); refetchCursos(); avisarCambioCoberturaPersona(personal?.rut); };

  const clearDocumentPrefill = () => {
    setPrefillClientId(null);
    setPrefillPrereqId(null);
    setPrefillNombreDocumento(undefined);
    setPrefillTipoDocumento(undefined);
    setPrefillReemplaza(null);
  };

  const handleAddDocument = () => {
    clearDocumentPrefill();
    setShowDocumentModal(true);
  };

  // Mismo exportador, aplicado a un documento y sus versiones anteriores. Sirve para
  // cualquier documento de la ficha, no solo para las actas de EPP.
  const handleExportarVersiones = async () => {
    const entry = historialDoc ? historialByDoc[String(historialDoc.id)] : null;
    if (exportandoVersiones || !historialDoc || !currentPersonal || !entry || entry.loading) return;
    setExportandoVersiones(true);
    try {
      const nombrePersona = standardizeName(`${currentPersonal.nombre || ''} ${currentPersonal.apellido || ''}`.trim())
        || (personal as any).nombres || 'Sin nombre';
      const total = entry.items.length + 1;
      const { incluidos, fallidos } = await descargarVersionesDocumentoPDF(
        { nombre: nombrePersona, rut: currentPersonal.rut || '' },
        historialDoc,
        entry.items
      );
      if (fallidos.length > 0) {
        toast.warn(`PDF generado con ${incluidos} de ${total} versiones. No se pudieron incluir: ${fallidos.map(f => f.nombre).join(', ')}`);
      } else {
        toast.success(`PDF generado con ${incluidos} versión${incluidos === 1 ? '' : 'es'}.`);
      }
    } catch (e: any) {
      console.error('Error exportando versiones del documento:', e);
      toast.error('No se pudo generar el PDF de las versiones.');
    } finally {
      setExportandoVersiones(false);
    }
  };

  const handleDocumentModalClose = () => {
    setShowDocumentModal(false);
    clearDocumentPrefill();
  };

  const handleDocumentSuccess = () => {
    refetchDocumentos();
    refreshBelrayPrereqs();
    avisarCambioCoberturaPersona(personal?.rut);
    onUpdate();
    // Refrescar la tabla de requisitos del cliente ACTIVO por el mismo camino que tras borrar
    // (antes usaba la prop clienteId y otro endpoint, y la fila no mostraba el documento nuevo).
    (async () => {
      const cid = clienteActivoId ?? clienteId;
      if (!cid || !personal?.rut) return;
      try {
        const fresh = await fetchMergedPrereqsCached(Number(cid), personal.rut, { fresh: true });
        const overridden = applyLocalOverrides(fresh);
        setPrereqDetailOverride(overridden);
        window.dispatchEvent(new CustomEvent('prereqDetailUpdated', { detail: { rut: personal.rut, clienteId: cid, detail: overridden } }));
      } catch (e) {
        console.warn('No se pudo refrescar detalle de prerrequisitos localmente', e);
      }
    })();
  };

  const handleDeleteAllForCourse = async (cursoId: number) => {
    const docsToDelete = personalDocuments.filter((d: any) => d.curso_id === cursoId);
    const validDocs = docsToDelete.filter((d: any) => d?.id !== undefined && d?.id !== null);
    
    if (validDocs.length === 0) {
      if (docsToDelete.length > 0) {
        alert('Los documentos de este curso no tienen IDs válidos y no pueden ser eliminados.');
      }
      return;
    }
    
    const invalidCount = docsToDelete.length - validDocs.length;
    if (invalidCount > 0) {
      console.warn(`⚠️ ${invalidCount} documento(s) del curso no tienen ID válido y serán omitidos`);
    }

    if (window.confirm(`¿Seguro que quieres eliminar ${validDocs.length} documento(s) asociado(s) a este curso?`)) {
      try {
        await Promise.all(validDocs.map((d: any) => deleteDocumentoWithClonesMutation.mutateAsync({ id: d.id, eliminarClones: false })));
        refetchDocumentos();
      } catch (error) {
        console.error("Error en eliminación masiva de documentos de curso:", error);
      }
    }
  };

  if (!isOpen) return null;

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'Activo':
        return 'bg-green-100 text-green-800';
      case 'Inactivo':
        return 'bg-red-100 text-red-800';
      case 'Suspendido':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-y-auto scrollbar-hide animate-slideInUp">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6 rounded-t-xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
            <div className="flex items-center space-x-4">
                <div className="relative group flex-shrink-0">
                <div className="h-16 w-16 sm:h-24 sm:w-24 rounded-full bg-white bg-opacity-20 flex items-center justify-center overflow-hidden">
                  {!imageError && hookHasImage ? (
                    <img
                      src={hookProfileImage ?? undefined}
                      alt="Foto de perfil"
                      className="h-full w-full rounded-full object-cover"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <User className="h-8 w-8 sm:h-12 sm:w-12 text-white" />
                  )}
                </div>
                {/* Botones de imagen en hover */}
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex space-x-1">
                    <label 
                      htmlFor="image-upload" 
                      className="cursor-pointer bg-blue-500 hover:bg-blue-600 p-2 rounded-full transition-colors"
                      title="Subir imagen"
                    >
                      <Upload className="h-4 w-4 text-white" />
                      <input
                        id="image-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={isUploading}
                      />
                    </label>
                    {!imageError && hookHasImage && (
                      <button
                        onClick={handleRemoveImage}
                        disabled={isDeleting}
                        className="bg-red-500 hover:bg-red-600 p-2 rounded-full transition-colors disabled:opacity-50"
                        title="Eliminar imagen"
                      >
                        <Trash2 className="h-4 w-4 text-white" />
                      </button>
                    )}
                  </div>
                </div>
                {/* Indicador de carga */}
                {(isUploading || isDeleting) && (
                  <div className="absolute inset-0 bg-black bg-opacity-70 rounded-full flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center flex-wrap gap-2">
                  <h2 className="text-lg sm:text-3xl font-bold break-words">
                    {/* Preferir nombre+apellido explícitos; 'nombres' de V2 solo contiene el primer nombre */}
                    {(currentPersonal as any).display_name || standardizeName(`${currentPersonal.nombre || ''} ${currentPersonal.apellido || ''}`.trim()) || (currentPersonal as any).nombres || 'Sin nombre'}
                  </h2>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs sm:text-sm font-medium flex-shrink-0 ${currentPersonal.activo ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}`}>
                    <Activity className="h-4 w-4 mr-1" />
                    {currentPersonal.estado_nombre}
                  </span>
                </div>
                {isEditing ? (
                  <input
                    type="text"
                    value={(editData as any).cargo ?? currentPersonal.cargo ?? ''}
                    onChange={(e) => handleInputChange('cargo', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded bg-white text-gray-900 mt-1"
                    placeholder="Cargo"
                  />
                ) : (
                  <p className="text-blue-100 text-sm sm:text-xl">
                    {currentPersonal.cargo || 'Sin cargo especificado'}
                  </p>
                )}
                {(() => {
                  const zonaLabel = displayValue(currentPersonal.zona_geografica, currentPersonal.rut);
                  if (zonaLabel && zonaLabel !== 'No asignada') {
                    return <p className="text-blue-200 text-sm">{zonaLabel}</p>;
                  }
                  return null;
                })()}

                <p className="text-blue-200 text-sm">RUT: {formatRUT(currentPersonal.rut)}</p>
              </div>
            </div>
            
            <div className="flex items-center flex-wrap gap-2 self-end sm:self-auto flex-shrink-0">
              {/* Botón para Cambiar Datos (Nombre y/o RUT) */}
              {!isEditing && (
                <button
                  onClick={() => setShowChangeDataModal(true)}
                  className="text-white transition-colors px-3 py-2 rounded-full hover:bg-white hover:bg-opacity-20 text-sm font-medium bg-white bg-opacity-10"
                  title="Cambiar nombre, apellido y/o RUT"
                >
                  Cambiar Datos
                </button>
              )}
              {isEditing ? (
                <>
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isLoading}
                    className="text-white hover:text-green-200 transition-colores p-2 rounded-full hover:bg-white hover:bg-opacity-20 disabled:opacity-50"
                  >
                    {updateMutation.isLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <Save className="h-6 w-6" />
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={updateMutation.isLoading}
                    className="text-white hover:text-red-200 transition-colores p-2 rounded-full hover:bg-white hover:bg-opacity-20 disabled:opacity-50"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-white hover:text-blue-200 transition-colores p-2 rounded-full hover:bg-white hover:bg-opacity-20"
                >
                  <Edit className="h-6 w-6" />
                </button>
              )}
              <button
                onClick={onClose}
                    className="text-white hover:text-blue-200 transition-colores p-2 rounded-full hover:bg-white hover:bg-opacity-20"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
          
            <div className="mt-6">
            {/* Profesión / Área / Supervisor / Tipo de asistencia */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div className="flex flex-col min-w-0">
                <span className="text-white text-sm opacity-90 font-medium">Profesión</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={(editData as any).profesion ?? currentPersonal.profesion ?? ''}
                    onChange={(e) => handleInputChange('profesion', e.target.value)}
                    className="px-2 py-1 text-sm border border-white/30 rounded bg-white/10 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                    placeholder="Profesión"
                  />
                ) : (
                  <span className="text-white font-semibold text-base drop-shadow-sm truncate">{displayValue(currentPersonal.profesion, currentPersonal.rut)}</span>
                )}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-white text-sm opacity-90 font-medium">Área</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={(editData as any).area ?? currentPersonal.area ?? ''}
                    onChange={(e) => handleInputChange('area', e.target.value)}
                    className="px-2 py-1 text-sm border border-white/30 rounded bg-white/10 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                    placeholder="Área"
                  />
                ) : (
                  <span className="text-white font-semibold text-base drop-shadow-sm truncate">{displayValue(currentPersonal.area, currentPersonal.rut)}</span>
                )}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-white text-sm opacity-90 font-medium">Supervisor</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={(editData as any).supervisor ?? currentPersonal.supervisor ?? ''}
                    onChange={(e) => handleInputChange('supervisor', e.target.value)}
                    className="px-2 py-1 text-sm border border-white/30 rounded bg-white/10 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                    placeholder="Supervisor"
                  />
                ) : (
                  <span className="text-white font-semibold text-base drop-shadow-sm truncate">{(formatSupervisorName(currentPersonal.supervisor) || displayValue(currentPersonal.supervisor, currentPersonal.rut))}</span>
                )}
              </div>
            </div>

            {/* Asignaciones (Carteras/Clientes/Nodos) eliminadas de la vista por petición */}
            <div className="mt-4">
              <label className="text-white text-sm opacity-90 font-medium">Clientes con servicios</label>
              <select
                value={selectedClienteId ?? ''}
                onChange={(e) => setSelectedClienteId(e.target.value ? Number(e.target.value) : null)}
                className="w-full mt-1 px-3 py-2 rounded bg-white/10 text-white placeholder-white/60 focus:outline-none border border-white/20"
              >
                <option value="">— Ninguno —</option>
                {(clientesServicios || []).map((c: any) => (
                  <option key={Number(c.cliente_id ?? c.id)} value={c.cliente_id ?? c.id} className="text-black">
                    {c.cliente_nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {errors.general && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {errors.general}
            </div>
          )}
          <div className={`${(prereqDetailLoading || prereqDetailInfo || prereqDetailError) ? '' : 'grid grid-cols-1 lg:grid-cols-2 gap-8'} items-start`}>
            {/* Columna 1: Información Personal */}
            <div className="space-y-4 flex flex-col">
              {/* Sección de Prerrequisitos en el lateral */}
              {prereqDetailLoading && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <LoadingSpinner />
                    <span className="text-sm text-blue-700">Cargando requisitos...</span>
                  </div>
                </div>
              )}
              
              {prereqDetailError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-red-500">⚠</span>
                    <div>
                      <p className="text-sm font-semibold text-red-800">Error al cargar requisitos</p>
                      <p className="text-xs text-red-600 mt-1">{prereqDetailError}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {(prereqDetailInfo || prereqDetailOverride) && !prereqDetailLoading && (() => {
                const prereqData = prereqDetailOverride || prereqDetailInfo;
                // Extraer datos de la nueva estructura
                const percentBackend = prereqData.prereq_percent ?? 0;
                // Usar contadores del backend directamente (ahora están alineados)
                let covered = prereqData.prereq_covered_count ?? 0;
                let total = prereqData.prereq_total ?? 0;
                const restantes = total - covered;

                // Combinar prerrequisitos cubiertos y faltantes en una sola lista
                let prerequisitosCubiertos = Array.isArray(prereqData.prerequisitos_cubiertos) ? prereqData.prerequisitos_cubiertos : [];
                const documentosFaltantes = Array.isArray(prereqData.documentos_faltantes) ? prereqData.documentos_faltantes : [];

                // Si el backend aún no envía la lista pero los contadores muestran cobertura, sintetizar desde documentos_usados/disponibles
                const coveredFromCounters = prereqData.prereq_covered_count ?? 0;
                if (prerequisitosCubiertos.length === 0 && coveredFromCounters > 0) {
                  const usados = Array.isArray(prereqData.documentos_usados) ? prereqData.documentos_usados : [];
                  const disponibles = Array.isArray(prereqData.documentos_disponibles) ? prereqData.documentos_disponibles : [];
                  const fuente = usados.length > 0 ? usados : disponibles.filter((d: any) => d && d.vencido !== true && d.vencido !== 'true');
                  const sintetizados = fuente.slice(0, coveredFromCounters).map((d: any, idx: number) => ({
                    prerequisito_id: d.prerequisito_id || d.id || idx,
                    prerequisito_tipo: d.tipo_normalizado || d.tipo_documento || d.tipo_original || 'documento',
                    prerequisito_descripcion: d.tipo_original || d.tipo_documento || d.tipo_normalizado || 'Documento cubierto',
                    documento_id: d.id,
                    documento_tipo_original: d.tipo_original || d.tipo_documento,
                    documento_tipo_normalizado: d.tipo_normalizado || d.tipo_documento,
                    documento_vencido: d.vencido || false,
                    sintetico: true
                  }));
                  prerequisitosCubiertos = sintetizados;
                }

                // Validación opcional en desarrollo: verificar consistencia
                if (process.env.NODE_ENV !== 'production') {
                  const expectedCovered = Array.isArray(prerequisitosCubiertos) ? prerequisitosCubiertos.length : 0;
                  const expectedTotal = expectedCovered + (Array.isArray(documentosFaltantes) ? documentosFaltantes.length : 0);
                  if (covered !== expectedCovered || total !== expectedTotal) {
                    console.warn('⚠️ Desalineación detectada entre contadores y listas (solo debug):', {
                      contadores: { covered, total, percent: percentBackend },
                      listas: { covered: expectedCovered, total: expectedTotal }
                    });
                  }
                }

                const computedPercent = total > 0
                  ? Math.round((covered / total) * 100)
                  : (typeof percentBackend === 'number' && percentBackend >= 0 ? percentBackend : 0);
                const progressColor = computedPercent >= 75 ? 'bg-green-500' : computedPercent > 50 ? 'bg-yellow-500' : 'bg-red-500';
                const progressBgColor = computedPercent >= 75 ? 'bg-green-100' : computedPercent > 50 ? 'bg-yellow-100' : 'bg-red-100';

                // Mapear prerequisitos cubiertos a formato unificado
                const cubiertosItems = prerequisitosCubiertos.map((p: any) => ({
                  prerequisito_id: p.prerequisito_id,
                  nombre: p.prerequisito_tipo || p.nombre || p.prerequisito_descripcion || 'Prerrequisito',
                  tipo_documento: p.prerequisito_tipo || p.nombre,
                  descripcion: p.prerequisito_descripcion,
                  documento_id: p.documento_id,
                  documento_tipo: p.documento_tipo_normalizado || p.documento_tipo_original,
                  vencido: p.documento_vencido || false,
                  cubierto: true,
                  required: true,
                  match_manual: p.match_manual === true || localMatchOverridesRef.current[String(p.prerequisito_id)]?.match_manual === true
                }));

                // Mapear documentos faltantes a formato unificado
                const faltantesItems = documentosFaltantes.map((d: any) => ({
                  prerequisito_id: d.prerequisito_id || d.id,
                  nombre: d.label || d.nombre || d.value || d.tipo_documento || 'Documento',
                  tipo_documento: d.tipo_documento || d.nombre || d.tipo,
                  descripcion: d.descripcion,
                  dias_duracion: d.dias_duracion ?? d.dias_validez,
                  cubierto: false,
                  required: d.required ?? d.es_obligatorio ?? true,
                  // Tiene el documento pero venció para este cliente (backend /faltantes).
                  alerta_vencido: d.alerta || null,
                  vencido_disponible: d.vencido_disponible || null
                }));

                // Combinar ambas listas: cubiertos primero, luego faltantes
                const todosPrerequisitos = [...cubiertosItems, ...faltantesItems];

                // Mostrar todos sin paginación
                const totalItems = todosPrerequisitos.length;
                const visibleDocs = todosPrerequisitos;

                return (
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-4 border border-amber-200 flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="h-5 w-5 text-amber-600" />
                      <div className="flex-1">
                        <h3 className="text-base font-bold text-amber-900">Estado de Requisitos</h3>
                        {(() => {
                          const selName = (clientesServicios || []).find((c: any) => Number(c.cliente_id ?? c.id) === Number(selectedClienteId))?.cliente_nombre || clienteName;
                          return selName ? <p className="text-xs text-amber-700 mt-0.5">Cliente: {selName}</p> : null;
                        })()}
                      </div>
                    </div>
                    
                    <div className="text-sm text-amber-800 mb-2">
                        <span className="font-semibold">
                        {`${covered}/${total}`} requisitos
                      </span>
                      <span className="ml-1">({computedPercent}%)</span>
                    </div>
                    
                    {/* Barra de progreso visual */}
                    <div className={`w-full ${progressBgColor} rounded-full h-2.5 mb-3`}>
                      <div 
                        className={`${progressColor} h-2.5 rounded-full transition-all duration-500`}
                        style={{ width: `${computedPercent}%` }}
                      />
                    </div>

                  {todosPrerequisitos && todosPrerequisitos.length > 0 && (() => {
                    // Buscar candidatos en documentos_disponibles para prereqs faltantes.
                    // Se excluyen los documentos que ya cubren OTRO prerrequisito (cubiertosItems):
                    // sin esto, dos prerrequisitos duplicados (ej. "Carnet de Identidad" global +
                    // "Carnet de Identidad" propio del cliente) reofrec\u00edan el mismo carnet ya
                    // asignado como si fuera un candidato sin reconocer ("\u26a0 sin alias"), cuando en
                    // realidad no hay ning\u00fan documento libre \u2014 el motor no reutiliza un documento
                    // para dos prerrequisitos (allowReuse=false).
                    const idsYaAsignados = new Set(cubiertosItems.map((c: any) => c.documento_id).filter(Boolean));
                    // También se excluyen los documentos vencidos: un documento vencido no cubre el
                    // requisito (no cuenta como cumplido), así que ofrecerlo como "candidato" solo
                    // repetía el nombre del requisito en la columna Documento ("⏰ Certificado de
                    // antece…") sin nada accionable — el requisito sigue en rojo y la única salida es
                    // subir uno vigente. La fila muestra "Sin documento" y el vencimiento se sigue
                    // viendo (con su causa) en las listas de documentos Personales/Cursos.
                    const disponibles: any[] = (Array.isArray(prereqData.documentos_disponibles) ? prereqData.documentos_disponibles : [])
                      .filter((d: any) => !idsYaAsignados.has(d.id) && d?.vencido !== true && d?.vencido !== 'true');
                    const normStr = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();
                    const findCandidato = (prereqNombre: string, tipodoc?: string) => {
                      const n = normStr(prereqNombre);
                      const t = normStr(tipodoc || prereqNombre);
                      return disponibles.find(d => {
                        const dKey = normStr(d.tipo_key || '');
                        const dOrig = normStr(d.tipo_original || '');
                        // dKey.includes(t) ya se autoprotege: una cadena corta no puede contener
                        // una más larga. Pero t.includes(dKey) NO — si dKey mide 2-3 caracteres
                        // (ej. alguien subió un documento literalmente llamado "CI" o "BA"), esa
                        // cadena aparece por pura coincidencia dentro de casi cualquier nombre
                        // largo ("trabajo".includes("ba") === true), marcando como "candidato" un
                        // documento que no tiene relación real con el requisito. Por eso dKey
                        // también necesita el mismo mínimo de 4 caracteres para esa dirección.
                        return dKey === t || dOrig === n || (t.length > 4 && dKey.includes(t)) || (t.length > 4 && dKey.length > 4 && t.includes(dKey));
                      }) ?? null;
                    };

                    const requisitoRows = visibleDocs.map((doc: any, idx: number) => {
                            const nombre = doc.nombre || 'Requisito';
                            const tipoDoc = doc.tipo_documento || doc.tipo || undefined;
                              const prereqId = doc.prerequisito_id ?? null;
                              const isCubierto = doc.cubierto || false;
                              const isVencido = doc.vencido || false;
                              const isManual = doc.match_manual === true;
                              // Debug: mostrar estado para ayudar a diagnosticar por qué no aparece el botón
                              try {
                                // eslint-disable-next-line no-console
                                console.debug('MatchDebug', { prereqId, isCubierto, isManual, docMatchManual: doc.match_manual, localOverride: localMatchOverridesRef.current[String(prereqId)] });
                              } catch (e) {}
                            const currentDocId = doc.documento_id; // ID del documento asignado

                            // Faltante porque el documento que lo cubriría VENCIÓ para este cliente:
                            // el backend manda el motivo; manda sobre la heurística "sin alias".
                            const alertaVencido: string | null = !isCubierto ? (doc.alerta_vencido || null) : null;
                            const vencidoDisp = alertaVencido ? doc.vencido_disponible : null;
                            // Para faltantes: buscar si hay un doc en disponibles que podría suplirlo
                            const candidato = !isCubierto && !alertaVencido ? findCandidato(nombre, tipoDoc) : null;
                            const docNombre = isCubierto
                              ? (doc.documento_tipo || nombre)
                              : candidato
                                ? (candidato.tipo_original || candidato.tipo_key)
                                : null;

                            const rowBg = isCubierto
                              ? (idx % 2 === 0 ? 'bg-white' : 'bg-green-50/30')
                              : (idx % 2 === 0 ? 'bg-white' : 'bg-red-50/20');

                            const indicatorDot = (
                              <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                                isCubierto
                                  ? isVencido ? 'bg-yellow-400' : 'bg-green-500'
                                  : 'bg-red-500'
                              }`} />
                            );

                            const requisitoLabel = (
                              <>
                                <span className={`font-medium ${isCubierto ? 'text-gray-700' : 'text-gray-800'}`}>
                                  {nombre}
                                </span>
                                {doc.required && !isCubierto && (
                                  <span className="ml-1.5 text-[10px] text-red-500 font-semibold">(Obligatorio)</span>
                                )}
                                {isVencido && (
                                  <span className="ml-1.5 text-[10px] text-yellow-600 font-semibold">⚠ Vencido</span>
                                )}
                                {isManual && (
                                  <span className="ml-1.5 text-[10px] text-purple-600 bg-purple-50 border border-purple-200 rounded px-1">manual</span>
                                )}
                              </>
                            );

                            const documentoCell = (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {/* Estado del documento */}
                                    {isCubierto ? (
                                      <span className="inline-flex items-center gap-1.5 text-green-700 font-medium text-xs">
                                        <span className="text-green-500 text-base leading-none">✓</span>
                                        <span className="truncate max-w-[180px]">{docNombre}</span>
                                      </span>
                                    ) : alertaVencido ? (
                                      <div className="w-full">
                                        <span className="inline-flex items-center gap-1.5 text-amber-700 text-xs font-medium">
                                          <span className="text-amber-500 text-base leading-none">⚠</span>
                                          <span className="truncate max-w-[180px]">{vencidoDisp?.nombre || 'Documento'}</span>
                                          <span className="text-[10px] text-amber-700 bg-amber-100 border border-amber-300 rounded px-1 shrink-0">vencido</span>
                                        </span>
                                        <p role="alert" className="mt-1 text-[11px] leading-snug text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                          {alertaVencido}
                                        </p>
                                      </div>
                                    ) : candidato ? (
                                      <span className="inline-flex items-center gap-1.5 text-amber-700 text-xs" title="Documento existente pero no reconocido como válido. Puede necesitar alias.">
                                        <span className="text-amber-500 text-base leading-none">⚠</span>
                                        <span className="italic truncate max-w-[120px]">{docNombre}</span>
                                        <span className="text-[10px] text-amber-500 bg-amber-50 border border-amber-200 rounded px-1 shrink-0">sin alias</span>
                                      </span>
                                    ) : (
                                      <span className="text-gray-300 text-xs">Sin documento</span>
                                    )}

                                    {/* Requisito cubierto: igual que en Pre-SISI. Ver (vista previa),
                                        Historial (vigente + versiones anteriores) y Reemplazar (sube una
                                        versión nueva; la actual pasa al historial y sus matches se mueven
                                        a la nueva). El editor de match manual, quitar match y eliminar ya
                                        no están acá: borrar sigue en las tarjetas de documentos. */}
                                    {isCubierto && prereqId && (
                                      <div className="w-full flex flex-wrap items-center gap-1 mt-1">
                                        {currentDocId && (() => {
                                          const docCompleto = todosDocumentos.find((d: any) => String(d?.id) === String(currentDocId))
                                            || { id: currentDocId, nombre_documento: docNombre };
                                          return (canViewRestricted || !isRestrictedDoc(docCompleto)) ? (
                                            <button
                                              onClick={e => { e.stopPropagation(); handlePreviewDocumento(docCompleto); }}
                                              className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
                                              title="Vista previa del documento"
                                              aria-label={`Ver el documento de ${nombre}`}
                                            >
                                              <Eye className="w-5 h-5" />
                                            </button>
                                          ) : (
                                            <span className="text-gray-400 p-1" title={RESTRICTED_HINT}><Lock className="w-5 h-5" /></span>
                                          );
                                        })()}
                                        {currentDocId && (
                                          <button
                                            onClick={e => {
                                              e.stopPropagation();
                                              const docCompleto = todosDocumentos.find((d: any) => String(d?.id) === String(currentDocId));
                                              abrirHistorialDocumento(docCompleto || { id: currentDocId, nombre_documento: docNombre });
                                            }}
                                            className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 transition-colors"
                                            title="Ver la versión vigente y las anteriores"
                                            aria-label="Historial de versiones"
                                          >
                                            <History className="w-5 h-5" />
                                          </button>
                                        )}
                                        <button
                                          onClick={e => {
                                            e.stopPropagation();
                                            const clientIdToUse = clienteActivoId ?? clienteId ?? prefillClientId ?? null;
                                            setPrefillClientId(clientIdToUse); setPrefillPrereqId(prereqId); setPrefillNombreDocumento(nombre); setPrefillTipoDocumento(tipoDoc);
                                            setPrefillReemplaza(currentDocId ? { id: Number(currentDocId), nombre: docNombre || nombre } : null);
                                            setShowDocumentModal(true);
                                          }}
                                          className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
                                          title="Subir una versión nueva: la actual pasa al historial"
                                          aria-label={`Reemplazar el documento de ${nombre}`}
                                        >
                                          <Upload className="w-5 h-5" />
                                        </button>
                                      </div>
                                    )}

                                    {/* Botones de acción para prerrequisitos faltantes sin match manual */}
                                    {!isManual && !isCubierto && (
                                      <div className="flex items-center gap-1 ml-auto shrink-0">
                                        {/* Match manual */}
                                        {(clienteActivoId && prereqId !== null ? (
                                          <button
                                            onClick={e => { e.stopPropagation(); openMatchManual(prereqId, nombre); }}
                                            className="text-purple-500 hover:text-purple-700 p-1 rounded hover:bg-purple-50 transition-colors"
                                            title="Asignar manualmente un documento existente a este prerrequisito"
                                            aria-label="Asignar documento existente"
                                          >
                                            <Link2 className="w-5 h-5" />
                                          </button>
                                        ) : (
                                          <span
                                            className="text-gray-300 p-1 rounded cursor-not-allowed"
                                            title="Match manual solo disponible cuando se abre desde un cliente específico"
                                          >
                                            <Link2 className="w-5 h-5" />
                                          </span>
                                        ))}
                                        {/* Subir documento */}
                                        <button
                                          onClick={e => { e.stopPropagation(); const clientIdToUse = clienteActivoId ?? clienteId ?? prefillClientId ?? null; setPrefillClientId(clientIdToUse); setPrefillPrereqId(prereqId); setPrefillNombreDocumento(nombre); setPrefillTipoDocumento(tipoDoc); setShowDocumentModal(true); }}
                                          className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
                                          title={`Subir documento para: ${nombre}`}
                                          aria-label={`Subir documento para ${nombre}`}
                                        >
                                          <Upload className="w-5 h-5" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                            );

                            return isMobile ? (
                              <div
                                key={`${prereqId}-${idx}`}
                                className={`p-3 rounded-lg border border-gray-100 ${rowBg}`}
                              >
                                <div className="flex items-start gap-2">
                                  <span className="mt-1 flex-shrink-0">{indicatorDot}</span>
                                  <div className="flex-1 min-w-0 text-sm">{requisitoLabel}</div>
                                </div>
                                <div className="mt-2 pl-4">{documentoCell}</div>
                              </div>
                            ) : (
                              <tr
                                key={`${prereqId}-${idx}`}
                                className={`border-b border-gray-100 last:border-b-0 ${rowBg}`}
                              >
                                <td className="px-3 py-2.5 text-center">{indicatorDot}</td>
                                <td className="px-3 py-2.5">{requisitoLabel}</td>
                                <td className="px-3 py-2.5">{documentoCell}</td>
                              </tr>
                            );
                          });

                    return (
                    <div className="mt-3 bg-white rounded-lg border border-amber-200 flex-1 overflow-auto">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-amber-100">
                        <h4 className="font-semibold text-amber-900 text-sm flex items-center gap-2">
                          <span>📋</span> Estado de Requisitos
                        </h4>
                        <span className="text-xs text-gray-500">{covered} cubiertos · {documentosFaltantes.length} faltantes</span>
                      </div>

                      {isMobile ? (
                        <div className="divide-y divide-gray-100">
                          {requisitoRows}
                        </div>
                      ) : (
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="text-left px-3 py-2 font-semibold text-gray-500 w-6"></th>
                              <th className="text-left px-3 py-2 font-semibold text-gray-600">Requisito</th>
                              <th className="text-left px-3 py-2 font-semibold text-gray-600">Documento</th>
                            </tr>
                          </thead>
                          <tbody>
                            {requisitoRows}
                          </tbody>
                        </table>
                      )}

                      {/* Mostrar prerrequisitos restantes */}
                      {total > 0 && covered !== undefined && restantes > 0 && (
                        <div className="mt-3 pt-3 px-3 pb-2 border-t border-amber-200">
                          <p className="text-xs text-amber-700">
                            <span className="font-semibold">
                              {restantes} requisito{restantes !== 1 ? 's' : ''} restante{restantes !== 1 ? 's' : ''}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                    );
                  })()}
                  </div>
                );
              })()}

              {!showOnlyPrerequisitos && (
                <>
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-600" />
                  Información Personal
                </h3>
                <div className="space-y-3">
                    <div className="flex flex-col md:flex-row md:space-x-4">
                      <div className="flex-1">
                        <label className="text-gray-600 font-medium">Nombre</label>
                        <input
                          type="text"
                          value={currentPersonal.nombre || ''}
                          readOnly
                          className="w-full px-2 py-1 border border-gray-200 rounded bg-gray-100 text-gray-700 mt-1 cursor-not-allowed"
                          title="Use el botón 'Cambiar Datos' para modificar"
                        />
                        <p className="text-xs text-gray-500 mt-1">Use el botón "Cambiar Datos" en el header</p>
                      </div>
                      <div className="flex-1 mt-3 md:mt-0">
                        <label className="text-gray-600 font-medium">Apellido</label>
                        <input
                          type="text"
                          value={currentPersonal.apellido || ''}
                          readOnly
                          className="w-full px-2 py-1 border border-gray-200 rounded bg-gray-100 text-gray-700 mt-1 cursor-not-allowed"
                          title="Use el botón 'Cambiar Datos' para modificar"
                        />
                        <p className="text-xs text-gray-500 mt-1">Use el botón "Cambiar Datos" en el header</p>
                      </div>
                    </div>

                    <div className="mt-3" />

                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">Sexo:</span>
                      {isEditing ? (
                        <select
                          value={editData.sexo || 'M'}
                          onChange={(e) => handleInputChange('sexo', e.target.value as 'M' | 'F')}
                          className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="M">Masculino</option>
                          <option value="F">Femenino</option>
                        </select>
                      ) : (
                        <span className="text-gray-900">{currentPersonal.sexo === 'M' ? 'Masculino' : 'Femenino'}</span>
                      )}
                    </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium">Fecha de Nacimiento:</span>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editData.fecha_nacimiento || (currentPersonal.fecha_nacimiento ? currentPersonal.fecha_nacimiento.split('T')[0] : '')}
                        onChange={(e) => { handleInputChange('fecha_nacimiento', e.target.value); const newAge = getAge(e.target.value).toString(); handleInputChange('edad', newAge); }}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <span className="text-gray-900">{currentPersonal.fecha_nacimiento ? formatDate(currentPersonal.fecha_nacimiento) : 'No especificada'}</span>
                    )}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium">Edad:</span>
                    {isEditing ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={editData.edad || (currentPersonal.fecha_nacimiento ? getAge(currentPersonal.fecha_nacimiento).toString() : '')}
                          readOnly
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded bg-gray-50 text-gray-700 cursor-not-allowed"
                          min="0"
                          max="120"
                        />
                        <span className="text-gray-500 text-sm">años</span>
                      </div>
                    ) : (
                      <span className="text-gray-900">{currentPersonal.fecha_nacimiento ? getAge(currentPersonal.fecha_nacimiento) : 'N/A'} años</span>
                    )}
                  </div>
                  {/* Centro de Costo (edición) */}
                  <div className="mt-3">
                    <label className="text-sm font-medium text-gray-700">Centro de Costo</label>
                    <div className="flex items-center space-x-2 mt-2">
                      <select
                        value={selectedCentroId ?? ''}
                        onChange={(e) => setSelectedCentroId(e.target.value)}
                        className="flex-1 px-3 py-2 border rounded"
                      >
                        <option value="">— Seleccionar centro —</option>
                        {centrosGrouped.length > 0 ? (
                          centrosGrouped.map(group => (
                            <optgroup key={group.label} label={group.label}>
                              {group.centros.map(c => (
                                <option key={String(c.id)} value={String(c.id)}>{c.nombre}</option>
                              ))}
                            </optgroup>
                          ))
                        ) : (
                          (availableCentros || []).map(c => (
                            <option key={String(c.id)} value={String(c.id)}>{c.nombre}</option>
                          ))
                        )}
                      </select>
                      <button onClick={handleUpdateCentro} className="px-3 py-2 bg-blue-600 text-white rounded">Guardar</button>
                      {/* Botón masivo eliminado - abrir CentroCostoMassModal desde otra parte si se requiere */}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Centro actual: {availableCentros.find(c => String(c.id) === String((currentPersonal as any)?.id_centro_costo))?.nombre || (currentPersonal as any)?.centro_costo || '—'}</p>
                  </div>

                  {/* Contacto directo (teléfono / email) */}
                  <div className="mt-3 border-t pt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Contacto</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <span className="text-gray-600 font-medium">Teléfono</span>
                        {isEditing ? (
                          <input
                            type="text"
                            value={(editData as any).telefono || currentPersonal.telefono || ''}
                            onChange={(e) => handleInputChange('telefono', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mt-1"
                          />
                        ) : (
                          <div className="text-gray-900 mt-1">{currentPersonal.telefono || 'Sin teléfono'}</div>
                        )}
                        {errors.telefono && (<p className="text-xs text-red-600 mt-1">{errors.telefono}</p>)}
                      </div>
                      <div>
                        <span className="text-gray-600 font-medium">Email</span>
                        {isEditing ? (
                          <input
                            type="email"
                            value={(editData as any).email || currentPersonal.email || ''}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mt-1"
                          />
                        ) : (
                          <div className="text-gray-900 mt-1">{currentPersonal.email || 'Sin email'}</div>
                        )}
                        {errors.email && (<p className="text-xs text-red-600 mt-1">{errors.email}</p>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contacto de Emergencia */}
              <div className="mt-4 bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="h-5 w-5 mr-2 text-red-600">⚑</span>
                  Contacto de Emergencia
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Nombre</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={((editData as any).contacto_emergencia?.nombre) ?? currentPersonal.contacto_emergencia?.nombre ?? ''}
                        onChange={(e) => handleInputChange('contacto_emergencia', { ...( (editData as any).contacto_emergencia || currentPersonal.contacto_emergencia || {} ), nombre: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Nombre contacto de emergencia"
                      />
                    ) : (
                      <span className="text-gray-900">{currentPersonal.contacto_emergencia?.nombre || 'Sin contacto de emergencia'}</span>
                    )}
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Vínculo</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={((editData as any).contacto_emergencia?.relacion) ?? currentPersonal.contacto_emergencia?.relacion ?? ''}
                        onChange={(e) => handleInputChange('contacto_emergencia', { ...( (editData as any).contacto_emergencia || currentPersonal.contacto_emergencia || {} ), relacion: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ej: Madre, Pareja, Hermano"
                      />
                    ) : (
                      <span className="text-gray-900">{currentPersonal.contacto_emergencia?.relacion || '-'}</span>
                    )}
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Teléfono</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={((editData as any).contacto_emergencia?.telefono) ?? currentPersonal.contacto_emergencia?.telefono ?? ''}
                        onChange={(e) => handleInputChange('contacto_emergencia', { ...( (editData as any).contacto_emergencia || currentPersonal.contacto_emergencia || {} ), telefono: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Teléfono contacto de emergencia"
                      />
                    ) : (
                      <span className="text-gray-900">{currentPersonal.contacto_emergencia?.telefono || '-'}</span>
                    )}
                  </div>

                  {errors['contacto_emergencia.nombre'] && <p className="text-xs text-red-600">{errors['contacto_emergencia.nombre']}</p>}
                  {errors['contacto_emergencia.telefono'] && <p className="text-xs text-red-600">{errors['contacto_emergencia.telefono']}</p>}
                </div>
              </div>

              {/* Ubicación */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-green-600" />
                  Ubicación
                </h3>
                <div className="space-y-3">
                      <div className="grid grid-cols-1 gap-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">Región:</span>
                          {isEditing ? (
                            <input
                              type="text"
                              value={(editData as any).ubicacion?.region || currentPersonal.ubicacion?.region || ''}
                              onChange={(e) => {
                                const prev = (editData as any).ubicacion || {};
                                handleInputChange('ubicacion', { ...prev, region: e.target.value });
                              }}
                              className="px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Región"
                            />
                          ) : (
                            <span className="text-gray-900">{displayValue(currentPersonal.ubicacion?.region, currentPersonal.rut)}</span>
                          )}
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">Ciudad:</span>
                          {isEditing ? (
                            <input
                              type="text"
                              value={(editData as any).ubicacion?.ciudad || currentPersonal.ubicacion?.ciudad || ''}
                              onChange={(e) => {
                                const prev = (editData as any).ubicacion || {};
                                handleInputChange('ubicacion', { ...prev, ciudad: e.target.value });
                              }}
                              className="px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Ciudad"
                            />
                          ) : (
                            <span className="text-gray-900">{displayValue(currentPersonal.ubicacion?.ciudad, currentPersonal.rut)}</span>
                          )}
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">Comuna:</span>
                          {isEditing ? (
                            <input
                              type="text"
                              value={(editData as any).ubicacion?.comuna || currentPersonal.ubicacion?.comuna || ''}
                              onChange={(e) => {
                                const prev = (editData as any).ubicacion || {};
                                handleInputChange('ubicacion', { ...prev, comuna: e.target.value });
                              }}
                              className="px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Comuna"
                            />
                          ) : (
                            <span className="text-gray-900">{displayValue(currentPersonal.ubicacion?.comuna, currentPersonal.rut)}</span>
                          )}
                        </div>
                      </div>
                </div>
              </div>

              {/* Licencias y Certificaciones */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Car className="h-5 w-5 mr-2 text-purple-600" />
                  Licencias y Certificaciones
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Licencia de Conducir:</span>
                    {isEditing ? (
                      <select
                        value={editData.licencia_conducir || ''}
                        onChange={(e) => handleInputChange('licencia_conducir', e.target.value)}
                        className={`px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.licencia_conducir ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="Sin licencia">Sin licencia</option>
                        <option value="A1">A1</option>
                        <option value="A2">A2</option>
                        <option value="A3">A3</option>
                        <option value="A4">A4</option>
                        <option value="A5">A5</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                      </select>
                    ) : (
                      <span className="text-gray-900 font-semibold">{displayValue(currentPersonal.licencia_conducir, currentPersonal.rut)}</span>
                    )}
                  </div>
                  {errors.licencia_conducir && (
                    <p className="text-xs text-red-600">{errors.licencia_conducir}</p>
                  )}
                </div>
              </div>

              {/* Tallas de Vestuario */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <ShirtIcon className="h-5 w-5 mr-2 text-orange-600" />
                  Tallas de Vestuario
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Talla de Zapatos:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editData.talla_zapatos || ''}
                        onChange={(e) => handleInputChange('talla_zapatos', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ej: 42, 38"
                      />
                    ) : (
                      <span className="text-gray-900 font-semibold">{displayValue(currentPersonal.talla_zapatos, currentPersonal.rut)}</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Talla de Pantalones:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editData.talla_pantalon || ''}
                        onChange={(e) => handleInputChange('talla_pantalon', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ej: M, 32, L"
                      />
                    ) : (
                      <span className="text-gray-900 font-semibold">{displayValue(currentPersonal.talla_pantalon, currentPersonal.rut)}</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Talla de Poleras:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editData.talla_poleras || ''}
                        onChange={(e) => handleInputChange('talla_poleras', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ej: S, M, L, XL"
                      />
                    ) : (
                      <span className="text-gray-900 font-semibold">{displayValue(currentPersonal.talla_poleras, currentPersonal.rut)}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Información del Sistema */}
              {/* Información del Sistema */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Activity className="h-5 w-5 mr-2 text-green-600" />
                  Estado del Personal
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Estado:</span>
                    {isEditing ? (
                      <select
                        value={editData.estado_id || currentPersonal.estado_id}
                        onChange={(e) => handleInputChange('estado_id', parseInt(e.target.value))}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={estadosLoading}
                      >
                        {estadosLoading ? (
                          <option>Cargando...</option>
                        ) : (
                          estadosData?.data
                            ?.filter((estado: any) => {
                              const estadosPermitidos = ['licencia medica', 'desvinculado', 'activo', 'vacaciones'];
                              return estadosPermitidos.some(permitido => permitido.toLowerCase() === estado.nombre.toLowerCase());
                            })
                            .map((estado: any) => {
                              const nombresFormato: { [key: string]: string } = {
                                'activo': 'Activo',
                                'vacaciones': 'Vacaciones',
                                'desvinculado': 'Desvinculado',
                                'licencia medica': 'Licencia Médica'
                              };
                              const nombreFormato = nombresFormato[estado.nombre.toLowerCase()] || estado.nombre;
                              return (
                                <option key={estado.id} value={estado.id}>{nombreFormato}</option>
                              );
                            })
                        )}
                      </select>
                    ) : (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${currentPersonal.activo ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}`}>
                        <Activity className="h-4 w-4 mr-2" />
                        {getEstadoNombreById(currentEstadoId)}
                      </span>
                    )}
                  </div>

                  {/* Rango de fechas para Vacaciones / Licencia médica → mantenimiento.personal_estados */}
                  {isEditing && estadoRequiereRango && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2">
                      <p className="text-xs text-amber-800">
                        Registra el rango de <strong>{selectedEstadoNombre === 'vacaciones' ? 'vacaciones' : 'licencia médica'}</strong>. Se guarda como evento con fecha de inicio y fin.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex flex-col">
                          <label className="text-xs font-medium text-amber-800 mb-1">Desde</label>
                          <input
                            type="date"
                            value={(editData.estado_temporal_desde ?? '').slice(0, 10)}
                            onChange={(e) => handleInputChange('estado_temporal_desde', e.target.value)}
                            className="px-2 py-1 border border-amber-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-xs font-medium text-amber-800 mb-1">Hasta <span className="text-amber-600">(opcional)</span></label>
                          <input
                            type="date"
                            value={(editData.estado_temporal_hasta ?? '').slice(0, 10)}
                            min={(editData.estado_temporal_desde ?? '').slice(0, 10) || undefined}
                            onChange={(e) => handleInputChange('estado_temporal_hasta', e.target.value)}
                            className="px-2 py-1 border border-amber-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                      </div>
                      {errors.estado_temporal_desde && (
                        <span className="block text-xs text-red-600">{errors.estado_temporal_desde}</span>
                      )}
                      {errors.estado_temporal_hasta && (
                        <span className="block text-xs text-red-600">{errors.estado_temporal_hasta}</span>
                      )}
                    </div>
                  )}
                  {!isEditing && estadoRequiereRango && eventoActivoEstado && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-medium">
                        {selectedEstadoNombre === 'vacaciones' ? 'Vacaciones' : 'Licencia médica'}:
                      </span>
                      <span className="text-gray-900 text-sm">
                        {(eventoActivoEstado.desde || '—').slice(0, 10)} → {(eventoActivoEstado.hasta || '—').slice(0, 10)}
                      </span>
                    </div>
                  )}

                  {(() => {
                    const fechaBase = currentPersonal.fecha_inicio_contrato || currentPersonal.created_at;
                    if (!fechaBase) return null;
                    const inicio = new Date(fechaBase);
                    const hoy = new Date();
                    let años = hoy.getFullYear() - inicio.getFullYear();
                    let meses = hoy.getMonth() - inicio.getMonth();
                    let dias = hoy.getDate() - inicio.getDate();
                    if (dias < 0) {
                      meses--;
                      dias += new Date(hoy.getFullYear(), hoy.getMonth(), 0).getDate();
                    }
                    if (meses < 0) { años--; meses += 12; }
                    const partes: string[] = [];
                    if (años > 0) partes.push(`${años} ${años === 1 ? 'año' : 'años'}`);
                    if (meses > 0) partes.push(`${meses} ${meses === 1 ? 'mes' : 'meses'}`);
                    if (años === 0 && dias > 0) partes.push(`${dias} ${dias === 1 ? 'día' : 'días'}`);
                    const texto = partes.join(' y ') || 'Menos de un día';
                    const fechaFormateada = inicio.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    return (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">Fecha de inicio de contrato:</span>
                          <span className="text-gray-900 text-sm">{fechaFormateada}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">Antigüedad:</span>
                          <span className="text-gray-900 text-sm">{texto}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Comentarios del Estado */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Comentarios del Estado</h3>
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editData.comentario_estado || ''}
                      onChange={(e) => handleInputChange('comentario_estado', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-800 text-sm leading-relaxed resize-none ${errors.comentario_estado ? 'border-red-500 focus:border-red-500' : 'border-blue-300 focus:border-blue-500'}`}
                      rows={4}
                      placeholder="Escriba comentarios adicionales sobre el estado del currentPersonal..."
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-blue-600">{editData.comentario_estado?.length || 0} caracteres</span>
                      <span className="text-xs text-blue-600">Máximo permitido: 1000 caracteres</span>
                    </div>
                    {errors.comentario_estado && (<p className="text-xs text-red-600 mt-1">{errors.comentario_estado}</p>)}
                  </div>
                ) : (
                  <div>
                    {currentPersonal.comentario_estado ? (
                      <p className="text-blue-800 text-sm leading-relaxed whitespace-pre-wrap">{currentPersonal.comentario_estado}</p>
                    ) : (
                      <p className="text-blue-600 text-sm italic">Sin comentarios adicionales</p>
                    )}
                  </div>
                )}
              </div>

              {/* ── Acreditación por Cliente ── */}
              <div className="border border-indigo-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setAcredOpen(v => !v)}
                  className="w-full flex items-center justify-between px-5 py-3 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-indigo-600" />
                    <span className="font-semibold text-indigo-900">Acreditación por Cliente</span>
                    {acredResumen && (
                      <span className="bg-indigo-200 text-indigo-800 text-xs px-2 py-0.5 rounded-full font-medium">
                        {acredResumen.acreditados} / {acredResumen.total_clientes}
                      </span>
                    )}
                  </div>
                  {acredOpen ? <ChevronUp className="h-4 w-4 text-indigo-600" /> : <ChevronDown className="h-4 w-4 text-indigo-600" />}
                </button>

                {acredOpen && (
                  <div className="p-4 bg-white space-y-2">
                    {/* Filtros: cartera + estado */}
                    <div className="flex gap-2">
                      <select
                        value={acredCarteraFiltro}
                        onChange={e => handleAcredCarteraChange(e.target.value)}
                        className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                      >
                        <option value="">Todas las carteras</option>
                        {Array.from(new Set(acredAllClientes.map((c: any) => (c.cartera_nombre || '').toUpperCase()).filter(Boolean))).sort().map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                        {(['todos', 'acreditados', 'pendientes'] as const).map(opt => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => handleAcredEstadoChange(opt)}
                            className={`px-2 py-1.5 transition-colors ${
                              acredEstadoFiltro === opt
                                ? 'bg-indigo-600 text-white font-medium'
                                : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {opt === 'todos' ? 'Todos' : opt === 'acreditados' ? '✅ Acred.' : '⏳ Pend.'}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Buscador cliente */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={acredSearch}
                        onChange={e => handleAcredSearchChange(e.target.value)}
                        placeholder="Buscar cliente..."
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      />
                    </div>

                    {/* Botón guardar — visible solo cuando hay cambios pendientes */}
                    {Object.keys(acredPendingChanges).length > 0 && (
                      <button
                        type="button"
                        disabled={acredSaving}
                        onClick={handleGuardarAcred}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      >
                        {acredSaving ? (
                          <><LoadingSpinner /><span>Guardando...</span></>
                        ) : (
                          <><Shield className="h-4 w-4" /><span>Guardar acreditación ({Object.keys(acredPendingChanges).length} cambio{Object.keys(acredPendingChanges).length !== 1 ? 's' : ''})</span></>
                        )}
                      </button>
                    )}

                    {acredLoading ? (
                      <div className="flex justify-center py-6"><LoadingSpinner /></div>
                    ) : acredClientes.length === 0 ? (
                      <p className="text-center text-gray-500 text-sm py-6">No se encontraron clientes.</p>
                    ) : (
                      <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
                        {acredClientes.map((c: any) => {
                          const id = Number(c.cliente_id);
                          const key = String(id);
                          const hasPending = key in acredPendingChanges;
                          const visualAcred = hasPending ? acredPendingChanges[key] : !!c.acreditado;
                          return (
                            <label
                              key={id}
                              className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer select-none transition-colors ${
                                hasPending ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={visualAcred}
                                disabled={acredSaving}
                                onChange={() => handleToggleAcred(id, visualAcred)}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:opacity-50"
                              />
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-gray-800 truncate block">{c.cliente_nombre}</span>
                                <span className="text-xs text-gray-500">{c.cartera_nombre}</span>
                              </div>
                              {hasPending && (
                                <span className="text-xs text-amber-600 font-medium whitespace-nowrap shrink-0">sin guardar</span>
                              )}
                              {!hasPending && visualAcred && c.fecha_acreditacion && (
                                <span className="text-xs text-green-600 whitespace-nowrap shrink-0">
                                  {new Date(c.fecha_acreditacion).toLocaleDateString('es-CL')}
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {/* Paginación */}
                    {acredTotalPages > 1 && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-500">
                          Página {acredPage} de {acredTotalPages} · {acredTotal} clientes
                        </span>
                        <div className="flex gap-2">
                          <button
                            disabled={acredPage <= 1 || acredLoading}
                            onClick={() => applyAcredFilter(acredAllClientes, acredSearch, acredPage - 1, acredCarteraFiltro, acredEstadoFiltro)}
                            className="px-3 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          >Anterior</button>
                          <button
                            disabled={acredPage >= acredTotalPages || acredLoading}
                            onClick={() => applyAcredFilter(acredAllClientes, acredSearch, acredPage + 1, acredCarteraFiltro, acredEstadoFiltro)}
                            className="px-3 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          >Siguiente</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* ──────────────────────────────────────────────────────────── */}

                </>
              )}
            </div>

            {/* Columna 2: Belray, Documentos y Cursos (orden visual via flex `order`) */}
            <div className="flex flex-col gap-4">
              {/* ── Documentos Belray (obligatorios para todas las personas) ── */}
              <div className="order-1 rounded-lg p-4 border-2 bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <button type="button" onClick={() => setBelrayExpandido(v => !v)} className="flex items-center flex-wrap gap-2 text-left min-w-0" title={belrayExpandido ? 'Minimizar' : 'Expandir'}>
                    {belrayExpandido ? <ChevronUp className="h-4 w-4 text-red-600 shrink-0" /> : <ChevronDown className="h-4 w-4 text-red-600 shrink-0" />}
                    <Shield className="h-5 w-5 text-red-600" />
                    <h3 className="text-lg font-semibold text-red-900">Documentos Belray</h3>
                    {requisitosBelray.length > 0 && (
                      <span className="bg-red-200 text-red-800 text-xs px-2 py-1 rounded-full">{requisitosBelray.filter((r: any) => r.cubierto).length}/{requisitosBelray.length} requisitos</span>
                    )}
                    <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide">Obligatorio</span>
                  </button>
                  {requisitosBelray.length > 0 && (
                    <select
                      value={areaFilterBelray}
                      onChange={(e) => setAreaFilterBelray(e.target.value as 'todas' | PrereqArea | 'sin_area')}
                      title="Filtrar por área responsable"
                      className="text-xs border border-red-300 rounded-lg px-2 py-1 bg-white text-red-900 shrink-0"
                    >
                      <option value="todas">Todas las áreas</option>
                      <option value="RRHH">🟣 RRHH</option>
                      <option value="Prevención">🟠 Prevención</option>
                      <option value="sin_area">— Sin área —</option>
                    </select>
                  )}
                </div>

                {belrayExpandido && (requisitosBelray.length > 0 ? (requisitosBelrayFiltered.length > 0 ? (
                  <div className="space-y-2.5">
                    {requisitosBelrayFiltered.map((r: any, i: number) => {
                      const areaResponsable = getArea(r.tipo);
                      const belrayDocObj = resolveDocumentoRequisito(r, todosDocumentos);
                      // Requisito marcado como cubierto pero cuyo documento no aparece en la
                      // lista de la persona (dado de baja, id inconsistente, etc.): hay que
                      // ofrecer igual una acción para arreglarlo, si no la fila queda muerta.
                      const belrayDocHuerfano = !!r.cubierto && !belrayDocObj;
                      const belrayDocRestringido = !!belrayDocObj && isRestrictedDoc(belrayDocObj);
                      const cardBorder = !r.cubierto
                        ? 'border-red-200'
                        : r.vencido
                          ? 'border-yellow-300'
                          : 'border-green-200';
                      return (
                      <div key={r.prereqId ?? i} className={`bg-white rounded-xl border ${cardBorder} p-3 shadow-sm hover:shadow-md transition-shadow`}>
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${r.cubierto ? (r.vencido ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600') : 'bg-red-100 text-red-500'}`}>
                            {r.cubierto ? (r.vencido ? '⚠' : '✓') : '○'}
                          </span>
                          <span className="font-semibold text-gray-900 text-sm truncate">{r.nombre}</span>
                          {areaResponsable && (
                            <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full font-medium ${AREA_BADGE_CLASSES[areaResponsable]}`} title="Área responsable">
                              {areaResponsable}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center justify-between gap-3 mt-2.5 pt-2.5 pl-9 border-t border-gray-100">
                          <div className="flex items-center gap-1.5 min-w-0 text-xs">
                            <FileText className={`w-3.5 h-3.5 shrink-0 ${r.cubierto ? 'text-gray-400' : 'text-gray-300'}`} />
                            {r.cubierto ? (
                              <span
                                className={`truncate ${belrayDocHuerfano ? 'text-amber-700' : 'text-gray-600'}`}
                                title={belrayDocHuerfano ? 'El requisito figura cubierto, pero el documento no está disponible en la ficha (puede estar dado de baja). Vuelve a subirlo o reasígnalo.' : undefined}
                              >
                                {r.documentoNombre || 'Documento asignado'}
                                {belrayDocHuerfano && ' · no disponible'}
                              </span>
                            ) : r.rechazado ? (
                              <span className="text-gray-500 italic" title="El match anterior se descartó a propósito; sube el documento correcto o asigna uno con el botón de match.">
                                En blanco · match descartado
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">Sin documento</span>
                            )}
                          </div>
                          <div className="shrink-0 flex items-center gap-1.5">
                            {belrayDocRestringido && !canAccessPrivileged && (
                              <span className="text-gray-400 p-1" title={RESTRICTED_HINT}>
                                <Lock className="h-5 w-5" />
                              </span>
                            )}
                            {belrayDocObj && (
                              <button
                                onClick={() => handlePreviewDocumento(belrayDocObj)}
                                className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
                                title="Visualización previa"
                              >
                                <Eye className="h-5 w-5" />
                              </button>
                            )}
                            {belrayDocObj && (canAccessPrivileged || !isRestrictedDoc(belrayDocObj)) && (
                              <button
                                onClick={() => handleEditDocumento(belrayDocObj, { clienteId: belrayClienteId, prereqId: r.prereqId })}
                                className="text-orange-500 hover:text-orange-700 p-1 rounded hover:bg-orange-50 transition-colors"
                                title="Editar documento"
                              >
                                <Edit className="h-5 w-5" />
                              </button>
                            )}
                            {belrayDocObj && canAccessPrivileged && (
                              <button
                                onClick={() => handleDeleteDocumento(belrayDocObj)}
                                disabled={deletingDocId === belrayDocObj.id}
                                className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-wait"
                                title="Eliminar documento"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            )}
                            {belrayDocObj && (
                              <button
                                onClick={() => abrirHistorialDocumento(belrayDocObj)}
                                className="p-1 rounded transition-colors text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
                                title="Ver historial de versiones anteriores"
                              >
                                <History className="h-5 w-5" />
                              </button>
                            )}
                            {r.prereqId != null && (
                              <button
                                onClick={() => openMatchManual(r.prereqId, r.nombre, r.cubierto, r.documentoId, belrayClienteId)}
                                className="text-purple-500 hover:text-purple-700 p-1 rounded hover:bg-purple-50 transition-colors"
                                title={r.cubierto ? `El match es incorrecto: asignar otro documento a "${r.nombre}" (reemplaza el actual)` : `Asignar un documento existente a: ${r.nombre}`}
                              >
                                <Link2 className="h-5 w-5" />
                              </button>
                            )}
                            {/* Dejar el requisito en blanco sin borrar el documento, para
                                poder subir después el correcto. */}
                            {r.cubierto && r.prereqId != null && (
                              <button
                                onClick={() => eliminarMatchManual(r.prereqId, belrayClienteId)}
                                className="text-amber-500 hover:text-amber-700 p-1 rounded hover:bg-amber-50 transition-colors"
                                title={`Quitar el match de "${r.nombre}": deja el requisito en blanco sin eliminar el documento`}
                              >
                                <XCircle className="h-5 w-5" />
                              </button>
                            )}
                            {/* Subir: cuando falta el documento y también cuando el requisito
                                figura cubierto pero su documento no aparece en la ficha
                                (ahí no hay nada que editar, así que sería una fila muerta). */}
                            {(!r.cubierto || belrayDocHuerfano) && (
                              <button
                                onClick={() => { setPrefillClientId(belrayClienteId); setPrefillPrereqId(r.prereqId); setPrefillNombreDocumento(r.nombre); setPrefillTipoDocumento(r.tipo); setShowDocumentModal(true); }}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white rounded-lg active:scale-95 transition shadow-sm ${belrayDocHuerfano ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`}
                                title={belrayDocHuerfano ? `Volver a subir el documento de: ${r.nombre}` : `Subir documento para: ${r.nombre}`}
                              >
                                <Upload className="w-3.5 h-3.5" /> Subir
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-red-700">
                    Ningún documento coincide con el filtro de área seleccionado.
                  </div>
                )) : (
                  <div className="flex items-start gap-3 bg-red-100 border border-red-300 rounded-lg p-3">
                    <span className="text-red-600 text-xl leading-none">⚠</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-red-800">
                        {belrayPrereqError ? 'No se pudo calcular la cobertura Belray' : 'Sin requisitos Belray cargados'}
                      </p>
                      <p className="text-xs text-red-600 mt-0.5">
                        {belrayPrereqError
                          ? `${belrayPrereqError} Los requisitos existen, pero no se pudo saber cuáles están cubiertos — no significa que la persona no tenga documentos.`
                          : 'No se pudieron cargar los requisitos del cliente Belray para esta persona.'}
                      </p>
                      {belrayPrereqError && (
                        <button
                          onClick={refreshBelrayPrereqs}
                          className="mt-2 px-2.5 py-1 text-xs font-semibold text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
                        >
                          Reintentar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Cursos y Certificaciones */}
              <div className="order-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center justify-between mb-4">
                  <button type="button" onClick={() => setCursosExpandido(v => !v)} className="flex items-center text-left min-w-0" title={cursosExpandido ? 'Minimizar' : 'Expandir'}>
                    {cursosExpandido ? <ChevronUp className="h-4 w-4 mr-1 text-purple-600 shrink-0" /> : <ChevronDown className="h-4 w-4 mr-1 text-purple-600 shrink-0" />}
                    <GraduationCap className="h-5 w-5 mr-2 text-purple-600 shrink-0" />
                    <h3 className="text-lg font-semibold text-purple-900">Cursos y Certificados</h3>
                    <span className="ml-2 bg-purple-200 text-purple-800 text-xs px-2 py-1 rounded-full">{courseDocuments.length} documento{courseDocuments.length !== 1 ? 's' : ''}</span>
                  </button>
                  <button onClick={handleAddCurso} className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-lg text-sm font-medium transition-colors flex items-center" title="Agregar curso">
                    <Plus className="h-4 w-4 mr-1" />
                    Agregar
                  </button>
                </div>


                {!cursosExpandido ? null : isLoadingDocumentos ? (
                  <div className="flex justify-center py-4"><LoadingSpinner /></div>
                ) : courseDocuments.length > 0 ? (
                  <div className={`space-y-3 ${!showAllCourses && courseDocuments.length > 2 ? 'max-h-72 overflow-y-auto pr-2' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={courseDocuments.every((d: any) => selectedDocs.has(d.id)) && courseDocuments.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedDocs(prev => {
                              const next = new Set(prev);
                              courseDocuments.forEach((d: any) => next.add(d.id));
                              return next;
                            });
                            else setSelectedDocs(prev => {
                              const next = new Set(prev);
                              courseDocuments.forEach((d: any) => next.delete(d.id));
                              return next;
                            });
                          }}
                        />
                        {canAccessPrivileged && (
                        <button
                          onClick={() => handleBulkDelete(courseDocuments.map((d: any) => d.id))}
                          disabled={!Array.from(selectedDocs).some(id => courseDocuments.find((d: any) => d.id === id))}
                          className="px-3 py-1 bg-red-500 text-white rounded-md text-sm disabled:opacity-50"
                        >
                          Eliminar seleccionados
                        </button>
                        )}
                        <button onClick={() => { courseDocuments.forEach((d: any) => toggleExpandDocument(d.id)); }} className="px-3 py-1 bg-gray-100 rounded-md text-sm">Alternar detalle</button>
                      </div>
                      <div className="text-sm text-purple-600">{courseDocuments.length} documento{courseDocuments.length !== 1 ? 's' : ''}</div>
                    </div>
                    { (showAllCourses ? courseDocuments : courseDocuments.slice(0, 2)).map((documento: any) => {
                      const isSelected = selectedDocs.has(documento.id);
                      const isExpanded = expandedDocs.has(documento.id);
                      return (
                        <div key={documento.id} className="bg-white rounded-lg border border-purple-200 p-4 hover:shadow-md transition-shadow flex items-start gap-4">
                          <div className="flex-shrink-0 pt-1">
                            <input type="checkbox" checked={isSelected} onChange={() => toggleSelectDocument(documento.id)} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{getDocumentIcon(documento.tipo_documento)}</span>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-purple-900 text-lg leading-snug">{documento.nombre_documento}</h4>
                                    {documento.es_clon && (
                                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 border border-blue-300 rounded-full font-medium">
                                        CLON
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-600 mt-1">{truncateFilename(documento.nombre_original || documento.nombre_archivo || documento.nombre_documento, isExpanded ? 400 : 80)}</div>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className={`ml-2 text-sm px-3 py-1 rounded-full border ${getDocumentColor(documento.tipo_documento)}`}>{documento.tipo_documento}</span>
                                <div className="mt-2 text-xs text-gray-500 flex items-center justify-end gap-1">
                                  {esDocumentoVencido(documento) && (
                                    <span className="text-[10px] font-bold text-red-500 bg-red-100 rounded-full px-1.5 py-0.5" title={explicarVencido(documento)}>⚠ VENCIDO</span>
                                  )}
                                  {documento.fecha_vencimiento
                                    ? `Vencimiento: ${formatDate(documento.fecha_vencimiento)}`
                                    : esDocumentoVencido(documento)
                                      ? explicarVencidoCorto(documento)
                                      : 'Vencimiento: Sin fecha'}
                                </div>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="mt-3 text-xs text-gray-700">
                                <p><strong>Archivo:</strong> {documento.nombre_original || documento.nombre_archivo || '—'}</p>
                                {documento.ruta_archivo && <p><strong>Ubicación:</strong> {documento.ruta_archivo}</p>}
                                {documento.fecha_vencimiento && <p><strong>Vencimiento:</strong> {new Date(documento.fecha_vencimiento).toLocaleDateString('es-CL')}</p>}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-center space-y-2">
                            {isRestrictedDoc(documento) && !canAccessPrivileged && (
                              <span className="text-gray-400 p-1" title={RESTRICTED_HINT}><Lock className="h-5 w-5" /></span>
                            )}
                            {(canViewRestricted || !isRestrictedDoc(documento)) && (
                              <button onClick={() => handlePreviewDocumento(documento)} className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colores" title="Visualización previa">
                                <Eye className="h-5 w-5" />
                              </button>
                            )}
                            {(canAccessPrivileged || !isRestrictedDoc(documento)) && (
                            <button onClick={() => handleEditDocumento(documento)} className="text-green-500 hover:text-green-700 p-1 rounded hover:bg-green-50 transition-colores" title="Editar documento"><Edit className="h-5 w-5" /></button>
                            )}
                            {(canAccessPrivileged || !isRestrictedDoc(documento)) && (
                            <button onClick={() => handleDeleteDocumento(documento)} disabled={deletingDocId === documento.id} className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colores disabled:opacity-40 disabled:cursor-wait" title="Eliminar documento"><Trash2 className="h-5 w-5" /></button>
                            )}
                            <button
                              onClick={() => abrirHistorialDocumento(documento)}
                              className="p-1 rounded transition-colors text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
                              title="Ver historial de versiones anteriores"
                            >
                              <History className="h-5 w-5" />
                            </button>
                            <button onClick={() => toggleExpandDocument(documento.id)} className="text-gray-400 hover:text-gray-600 text-xs">{isExpanded ? 'Cerrar' : 'Ver más'}</button>
                          </div>
                        </div>
                      );
                    })}
                      {courseDocuments.length > 2 && (
                        <div className="flex justify-center mt-2">
                          <button
                            onClick={() => setShowAllCourses(prev => !prev)}
                            className="px-3 py-1 bg-gray-100 rounded-md text-sm hover:bg-gray-200"
                          >
                            {showAllCourses ? 'Mostrar menos' : `Mostrar todos (${courseDocuments.length})`}
                          </button>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <GraduationCap className="h-16 w-16 text-purple-300 mx-auto mb-4" />
                    <h4 className="text-purple-700 font-medium mb-2">Sin documentos de cursos</h4>
                    <p className="text-purple-600 text-sm mb-4">{standardizeName(`${currentPersonal.nombre || ''} ${currentPersonal.apellido || ''}`.trim()) || (personal as any).nombres || 'Sin nombre'} no tiene documentos de cursos o certificaciones</p>
                    <div className="flex justify-center">
                      <button onClick={handleAddCurso} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colores">
                        <Plus className="h-4 w-4 mr-1" />
                        Agregar curso
                      </button>
                    </div>
                  </div>
                )}
              </div>

              
              {/* Documentación Personal */}
              <div className="order-2 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-200">
                <div className="flex items-center justify-between mb-4">
                  <button type="button" onClick={() => setDocumentosExpandido(v => !v)} className="flex items-center text-left min-w-0" title={documentosExpandido ? 'Minimizar' : 'Expandir'}>
                    {documentosExpandido ? <ChevronUp className="h-4 w-4 mr-1 text-orange-600 shrink-0" /> : <ChevronDown className="h-4 w-4 mr-1 text-orange-600 shrink-0" />}
                    <FileText className="h-5 w-5 mr-2 text-orange-600 shrink-0" />
                    <h3 className="text-lg font-semibold text-orange-900">Documentación Personal</h3>
                    <span className="ml-2 bg-orange-200 text-orange-800 text-xs px-2 py-1 rounded-full">
                      {personalDocumentsFiltered.length}{(areaFilterDocs !== 'todas' || docSearchTerm.trim()) ? ` de ${personalDocuments.length}` : ''} documento{personalDocumentsFiltered.length !== 1 ? 's' : ''}
                    </span>
                  </button>
                  <div className="flex items-center gap-2">
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-orange-400 pointer-events-none" />
                      <input
                        type="text"
                        value={docSearchTerm}
                        onChange={(e) => setDocSearchTerm(e.target.value)}
                        placeholder="Buscar documento..."
                        title="Buscar por nombre, tipo o descripción"
                        className="text-xs border border-orange-300 rounded-lg pl-7 pr-2 py-1 bg-white text-orange-900 placeholder-orange-300 w-40 focus:outline-none focus:ring-1 focus:ring-orange-400"
                      />
                    </div>
                    <select
                      value={areaFilterDocs}
                      onChange={(e) => setAreaFilterDocs(e.target.value as 'todas' | PrereqArea | 'sin_area')}
                      onClick={(e) => e.stopPropagation()}
                      title="Filtrar por área responsable"
                      className="text-xs border border-orange-300 rounded-lg px-2 py-1 bg-white text-orange-900"
                    >
                      <option value="todas">Todas las áreas</option>
                      <option value="RRHH">🟣 RRHH</option>
                      <option value="Prevención">🟠 Prevención</option>
                      <option value="sin_area">— Sin área —</option>
                    </select>
                    <button onClick={handleAddDocument} className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded-lg text-sm font-medium transition-colores flex items-center" title="Agregar documento">
                      <Upload className="h-4 w-4 mr-1" />
                      Subir
                    </button>
                  </div>
                </div>


                {!documentosExpandido ? null : isLoadingDocumentos ? (
                  <div className="flex justify-center py-4"><LoadingSpinner /></div>
                ) : personalDocuments.length > 0 && personalDocumentsFiltered.length === 0 ? (
                  <div className="text-center py-6 text-sm text-orange-600">
                    Ningún documento coincide con la búsqueda o el filtro de área seleccionado.
                  </div>
                ) : personalDocuments.length > 0 ? (
                  <div className={`space-y-3 ${!showAllPersonalDocs && personalDocumentsFiltered.length > 2 ? 'max-h-72 overflow-y-auto pr-2' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={personalDocumentsFiltered.every((d: any) => selectedDocs.has(d.id)) && personalDocumentsFiltered.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedDocs(prev => {
                              const next = new Set(prev);
                              personalDocumentsFiltered.forEach((d: any) => next.add(d.id));
                              return next;
                            });
                            else setSelectedDocs(prev => {
                              const next = new Set(prev);
                              personalDocumentsFiltered.forEach((d: any) => next.delete(d.id));
                              return next;
                            });
                          }}
                        />
                        {canAccessPrivileged && (
                        <button
                          onClick={() => handleBulkDelete(personalDocumentsFiltered.map((d: any) => d.id))}
                          disabled={!Array.from(selectedDocs).some(id => personalDocumentsFiltered.find((d: any) => d.id === id))}
                          className="px-3 py-1 bg-red-500 text-white rounded-md text-sm disabled:opacity-50"
                        >
                          Eliminar seleccionados
                        </button>
                        )}
                        <button onClick={() => { personalDocumentsFiltered.forEach((d: any) => toggleExpandDocument(d.id)); }} className="px-3 py-1 bg-gray-100 rounded-md text-sm">Alternar detalle</button>
                      </div>
                      <div className="text-sm text-orange-600">{personalDocumentsFiltered.length} documento{personalDocumentsFiltered.length !== 1 ? 's' : ''}</div>
                    </div>
                    { (showAllPersonalDocs ? personalDocumentsFiltered : personalDocumentsFiltered.slice(0, 2)).map((documento: any) => {
                      const isSelected = selectedDocs.has(documento.id);
                      const isExpanded = expandedDocs.has(documento.id);
                      const docArea = getArea(documento.tipo_documento);
                      return (
                        <div key={documento.id} className="bg-white rounded-lg border border-orange-200 p-4 hover:shadow-md transition-shadow flex items-start gap-4">
                          <div className="flex-shrink-0 pt-1">
                            <input type="checkbox" checked={isSelected} onChange={() => toggleSelectDocument(documento.id)} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{getDocumentIcon(documento.tipo_documento)}</span>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-orange-900 text-lg leading-snug">{documento.nombre_documento}</h4>
                                    {documento.es_clon && (
                                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 border border-blue-300 rounded-full font-medium">
                                        CLON
                                      </span>
                                    )}
                                    {docArea && (
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${AREA_BADGE_CLASSES[docArea]}`} title="Área responsable">
                                        {docArea}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-600 mt-1">{truncateFilename(documento.nombre_original || documento.nombre_archivo || documento.nombre_documento, isExpanded ? 400 : 80)}</div>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className={`ml-2 text-sm px-3 py-1 rounded-full border ${getDocumentColor(documento.tipo_documento)}`}>{documento.tipo_documento}</span>
                                <div className="mt-2 text-xs text-gray-500 flex items-center justify-end gap-1">
                                  {esDocumentoVencido(documento) && (
                                    <span className="text-[10px] font-bold text-red-500 bg-red-100 rounded-full px-1.5 py-0.5" title={explicarVencido(documento)}>⚠ VENCIDO</span>
                                  )}
                                  {documento.fecha_vencimiento
                                    ? `Vencimiento: ${formatDate(documento.fecha_vencimiento)}`
                                    : esDocumentoVencido(documento)
                                      ? explicarVencidoCorto(documento)
                                      : 'Vencimiento: Sin fecha'}
                                </div>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="mt-3 text-xs text-gray-700">
                                <p><strong>Archivo:</strong> {documento.nombre_original || documento.nombre_archivo || '—'}</p>
                                {documento.ruta_archivo && <p><strong>Ubicación:</strong> {documento.ruta_archivo}</p>}
                                {documento.fecha_vencimiento && <p><strong>Vencimiento:</strong> {new Date(documento.fecha_vencimiento).toLocaleDateString('es-CL')}</p>}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-center space-y-2">
                            {isRestrictedDoc(documento) && !canAccessPrivileged && (
                              <span className="text-gray-400 p-1" title={RESTRICTED_HINT}><Lock className="h-5 w-5" /></span>
                            )}
                            {(canViewRestricted || !isRestrictedDoc(documento)) && (
                              <button onClick={() => handlePreviewDocumento(documento)} className="text-green-500 hover:text-green-700 p-1 rounded hover:bg-green-50 transition-colores" title="Visualización previa">
                                <Eye className="h-5 w-5" />
                              </button>
                            )}
                            {(canAccessPrivileged || !isRestrictedDoc(documento)) && (
                            <button onClick={() => handleEditDocumento(documento)} className="text-orange-500 hover:text-orange-700 p-1 rounded hover:bg-orange-50 transition-colores" title="Editar documento"><Edit className="h-5 w-5" /></button>
                            )}
                            {(canAccessPrivileged || !isRestrictedDoc(documento)) && (
                            <button
                              onClick={() => handleDeleteDocumento(documento)}
                              disabled={deletingDocId === documento.id}
                              className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colores disabled:opacity-40 disabled:cursor-wait"
                              title="Eliminar documento"
                            ><Trash2 className="h-5 w-5" /></button>
                            )}
                            <button
                              onClick={() => abrirHistorialDocumento(documento)}
                              className="p-1 rounded transition-colors text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
                              title="Ver historial de versiones anteriores"
                            >
                              <History className="h-5 w-5" />
                            </button>
                            <button onClick={() => toggleExpandDocument(documento.id)} className="text-gray-400 hover:text-gray-600 text-xs">{isExpanded ? 'Cerrar' : 'Ver más'}</button>
                          </div>
                        </div>
                      );
                    })}
                      {personalDocumentsFiltered.length > 2 && (
                        <div className="flex justify-center mt-2">
                          <button
                            onClick={() => setShowAllPersonalDocs(prev => !prev)}
                            className="px-3 py-1 bg-gray-100 rounded-md text-sm hover:bg-gray-200"
                          >
                            {showAllPersonalDocs ? 'Mostrar menos' : `Mostrar todos (${personalDocumentsFiltered.length})`}
                          </button>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-16 w-16 text-orange-300 mx-auto mb-4" />
                    <h4 className="text-orange-700 font-medium mb-2">Sin documentos personales</h4>
                    <p className="text-orange-600 text-sm mb-4">{standardizeName(`${currentPersonal.nombre || ''} ${currentPersonal.apellido || ''}`.trim()) || (personal as any).nombres || 'Sin nombre'} no tiene documentos personales subidos</p>
                    <button onClick={handleAddDocument} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colores">
                      <Upload className="h-4 w-4 mr-1 inline" />
                      Subir primer documento
                    </button>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 bg-gray-50 px-6 py-4 rounded-b-xl border-t shadow-[0_-2px_8px_rgba(0,0,0,0.05)]">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <span className="font-medium">RUT:</span> {formatRUT(currentPersonal.rut)}
            </div>
            <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colores font-medium">Cerrar</button>
          </div>
        </div>
      </div>

      {/* Modal de Cursos */}
      <CursoModal
        isOpen={showCursoModal}
        onClose={handleCursoModalClose}
        onSuccess={handleCursoSuccess}
        curso={editingCurso}
        rutPersona={personal?.rut || ''}
        nombrePersona={`${personal?.nombre || 'Sin nombre'} ${personal?.apellido || 'Sin apellido'}`.trim()}
        personalId={personal?.id || ''}
      />

      {/* Modal de Documentos (Nuevo SubirDocumentoModal) */}
      <SubirDocumentoModal
        isOpen={showDocumentModal}
        onClose={handleDocumentModalClose}
        onSuccess={handleDocumentSuccess}
        rutPersona={personal?.rut || ''}
        nombrePersona={`${personal?.nombre || 'Sin nombre'} ${personal?.apellido || 'Sin apellido'}`.trim()}
        initialClientId={prefillClientId ?? clienteId ?? null}
        initialClientPrereq={prefillPrereqId}
        initialNombreDocumento={prefillNombreDocumento}
        initialTipoDocumento={prefillTipoDocumento}
        reemplazaDocumentoId={prefillReemplaza?.id ?? null}
        reemplazaNombre={prefillReemplaza?.nombre}
      />

      {/* Modal de Documentos de Cursos */}
      <CourseDocumentModal
        isOpen={showCourseDocumentModal}
        onClose={handleCourseDocumentModalClose}
        onSuccess={handleCourseDocumentSuccess}
        rutPersona={personal?.rut || ''}
        nombrePersona={`${personal?.nombre || 'Sin nombre'} ${personal?.apellido || 'Sin apellido'}`.trim()}
        personalId={personal?.id || ''}
        cursoNombre={selectedCurso?.nombre_curso}
      />

      {/* Modal de Edición de Documentos */}
      <EditDocumentModal
        isOpen={showEditDocumentModal}
        onClose={handleEditDocumentModalClose}
        onSuccess={handleEditDocumentSuccess}
        documento={editingDocumento}
        personalId={personal?.id || ''}
        defaultClienteId={editingDocumentoContext?.clienteId ?? null}
        defaultPrereqId={editingDocumentoContext?.prereqId ?? null}
      />

      {/* Modal de Clonación de Documentos */}
      <CloneDocumentModal
        isOpen={showCloneDocumentModal}
        onClose={() => { setShowCloneDocumentModal(false); setCloningDocumento(null); }}
        onSuccess={() => { refetchDocumentos(); avisarCambioCoberturaPersona(personal?.rut); setShowCloneDocumentModal(false); setCloningDocumento(null); }}
        documento={cloningDocumento}
      />

      {/* Modal de Vista Previa de Documentos.
          El wrapper con z-index no es decorativo (mismo caso que en presisi/ModalVencimientos):
          DocumentPreviewModal se pinta con `z-60`, una clase que NO existe en la config de
          Tailwind de este proyecto (llega hasta z-50), así que se queda en z-index:auto. Sin
          esto, la vista previa abierta desde la ventana de versiones (z-[60]) quedaría por
          debajo de ella. */}
      <div className="relative z-[70]">
        <DocumentPreviewModal
          isOpen={showPreviewModal}
          onClose={() => { setShowPreviewModal(false); setPreviewDocumento(null); }}
          documento={previewDocumento}
        />
      </div>

      {/* ── Modal match manual ─────────────────────────────────────────── */}
      {matchManualOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={() => !matchManualSaving && setMatchManualOpen(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-purple-100 bg-gradient-to-r from-purple-50 via-purple-50/60 to-white">
              <div className="flex items-center gap-3 min-w-0">
                <span className="shrink-0 w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
                  <Link2 className="w-4 h-4 text-purple-600" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-gray-900">
                    {matchManualOpen?.isEditing ? 'Editar Match Manual' : 'Match Manual'}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    Prerrequisito: <span className="font-semibold text-purple-700">{matchManualOpen?.prereqNombre}</span>
                  </p>
                </div>
              </div>
              <button onClick={() => setMatchManualOpen(null)} disabled={matchManualSaving} className="shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-30 p-1.5 rounded-full hover:bg-white/70 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Instrucción */}
            <div className="px-5 pt-3 pb-1">
              <div className="flex items-start gap-2 text-xs text-purple-800 bg-purple-50 border border-purple-100 rounded-lg px-3 py-2">
                <span className="text-sm leading-none shrink-0">{matchManualOpen?.isEditing ? '✏️' : '👇'}</span>
                <p>
                  {matchManualOpen?.isEditing
                    ? 'Selecciona un nuevo documento para reemplazar la asignación actual. El cambio se guardará automáticamente.'
                    : 'Haz click en el documento que cubre este prerrequisito.'}
                </p>
              </div>
            </div>


            {/* Buscador */}
            {!matchManualLoading && matchManualCandidatos.length > 0 && (
              <div className="px-5 pt-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={matchManualSearch}
                    onChange={(e) => setMatchManualSearch(e.target.value)}
                    placeholder="Buscar documento..."
                    className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  {matchManualSearch && (
                    <button
                      onClick={() => setMatchManualSearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Limpiar búsqueda"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Tabla de documentos */}
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {matchManualLoading ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <LoadingSpinner size="sm" />
                  <p className="text-xs text-gray-400">Cargando documentos del trabajador…</p>
                </div>
              ) : matchManualCandidatos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-400">
                  <FileText className="w-8 h-8 opacity-30" />
                  <p className="text-sm">No hay documentos disponibles para este trabajador.</p>
                </div>
              ) : (() => {
                const q = matchManualSearch.toLowerCase().trim();
                const filtered = matchManualCandidatos.filter((doc: any) => {
                  if (!q) return true;
                  const label = String(doc._displayName || doc.tipo_original || doc.tipo_documento || doc.tipo_normalizado || doc.nombre || '').toLowerCase();
                  const archivo = String(doc.nombre_archivo || '').toLowerCase();
                  return label.includes(q) || archivo.includes(q);
                });

                if (filtered.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-400">
                      <Search className="w-8 h-8 opacity-30" />
                      <p className="text-sm">Ningún documento coincide con "{matchManualSearch}".</p>
                    </div>
                  );
                }

                return (
                  <div className="overflow-x-auto rounded-lg border border-gray-100">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
                          <th className="py-2 pl-3 pr-2 font-semibold w-9"></th>
                          <th className="py-2 pr-2 font-semibold">Documento</th>
                          <th className="py-2 pr-2 font-semibold whitespace-nowrap">Vencimiento</th>
                          <th className="py-2 pr-3 font-semibold text-right">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((doc: any) => {
                          const docId = String(doc.id ?? doc.documento_id);
                          const label = doc._displayName || doc.tipo_original || doc.tipo_documento || doc.tipo_normalizado || doc.nombre || `Doc #${docId}`;
                          const fecha = doc.fecha_vencimiento ? doc.fecha_vencimiento.slice(0, 10) : null;
                          const vencido = doc.vencido || false;
                          const yaAsignado = doc.ya_asignado === true;
                          const isSavingThis = matchManualSavingDocId === docId;
                          const isCurrentDoc = matchManualOpen?.currentDocId && String(matchManualOpen.currentDocId) === docId;
                          const disabled = matchManualSaving && !isSavingThis;

                          return (
                            <tr
                              key={docId}
                              onClick={() => !matchManualSaving && guardarMatchManual(docId)}
                              className={[
                                'border-b border-gray-100 last:border-0 transition-colors',
                                isSavingThis ? 'bg-purple-50' : isCurrentDoc ? 'bg-blue-50/70' : vencido ? 'bg-red-50/40 hover:bg-red-50' : 'hover:bg-purple-50/50',
                                disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
                              ].join(' ')}
                            >
                              <td className="py-2 pl-3 pr-2">
                                <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                                  isSavingThis ? 'bg-purple-200' : isCurrentDoc ? 'bg-blue-200' : vencido ? 'bg-red-100' : 'bg-gray-100'
                                }`}>
                                  {isSavingThis
                                    ? <LoadingSpinner size="sm" />
                                    : isCurrentDoc
                                      ? <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                      : <FileText className={`w-3.5 h-3.5 ${vencido ? 'text-red-400' : 'text-gray-500'}`} />}
                                </span>
                              </td>
                              <td className="py-2 pr-2 min-w-0">
                                <p className={`font-semibold break-words ${vencido ? 'text-red-700' : isCurrentDoc ? 'text-blue-700' : 'text-gray-800'}`}>
                                  {label}
                                  {isCurrentDoc && <span className="ml-1.5 text-[10px] text-blue-600 bg-blue-100 rounded px-1.5 py-0.5 align-middle">ACTUAL</span>}
                                </p>
                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                  {vencido && <span className="text-[10px] font-bold text-red-500 bg-red-100 rounded-full px-1.5 py-0.5">⚠ VENCIDO</span>}
                                  {yaAsignado && !isCurrentDoc && <span className="text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">ya asignado</span>}
                                  {doc.ya_cubre_tipo && <span className="text-[10px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5">ya acredita por tipo</span>}
                                  {doc.nombre_archivo && <span className="text-[10px] text-gray-300 break-all">{doc.nombre_archivo}</span>}
                                </div>
                              </td>
                              <td className="py-2 pr-2 whitespace-nowrap text-xs text-gray-500">{fecha || '—'}</td>
                              <td className="py-2 pr-3 text-right">
                                {!isSavingThis && (
                                  <span
                                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                                      isCurrentDoc ? 'text-blue-500 bg-blue-100' : 'text-purple-500 bg-purple-50 hover:bg-purple-100'
                                    }`}
                                    title={isCurrentDoc ? 'Documento actualmente asignado' : 'Asignar este documento'}
                                  >
                                    {isCurrentDoc ? <Check className="w-4 h-4" /> : <ArrowRightCircle className="w-4 h-4" />}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/60 flex justify-end">
              <button
                onClick={() => setMatchManualOpen(null)}
                disabled={matchManualSaving}
                className="px-4 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40 transition"
              >Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Ventana de versiones de un documento ──────────────────────────────────
          Se abre desde el reloj de cualquier tarjeta (Belray, Documentación Personal y
          Cursos): muestra la versión vigente y las anteriores, y las exporta juntas a un
          PDF. Antes esto se desplegaba en línea dentro de cada tarjeta. */}
      {historialDoc && (() => {
        const entry = historialByDoc[String(historialDoc.id)];
        const claseDoc = claseEPP(historialDoc) as ClaseEPP | null;
        const cargando = !entry || entry.loading;
        const versiones = entry && !entry.loading && !entry.error ? entry.items : [];

        // Fila de la lista, con el mismo formato para la vigente y las anteriores.
        const fila = (d: any, esVigente: boolean) => {
          const fecha = esVigente
            ? (d.fecha_emision || d.fecha_subida)
            : (d.fecha_creacion || d.created_at || d.fecha_subida || d.updated_at);
          const visible = canViewRestricted || !isRestrictedDoc(d);
          const archivo = d.nombre_original || d.nombre_archivo || '—';
          return (
            <div key={d.id} className={`bg-white rounded-lg border p-3 ${esVigente ? 'border-emerald-300' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center flex-wrap gap-2">
                    <span className="text-sm font-semibold text-gray-800 truncate" title={d.nombre_documento}>
                      {d.nombre_documento || d.nombre_original || `Documento ${d.id}`}
                    </span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${esVigente ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-gray-100 text-gray-600 border-gray-300'}`}>
                      {esVigente ? 'Vigente' : 'Versión anterior'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {fecha ? formatDate(fecha) : 'Sin fecha'}
                    {' · '}
                    {truncateFilename(archivo, 50)}
                    {/* El historial agrupa por nombre, no por tipo (ver GET /:id/historial):
                        el tipo es lo único que distingue dos versiones que se llaman igual.
                        Va dentro del mismo gate que el botón de ver para no revelar de un
                        documento restringido más de lo que ya se muestra. */}
                    {visible && d.tipo_documento ? ` · ${d.tipo_documento}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {isRestrictedDoc(d) && !canAccessPrivileged && (
                    <span className="text-gray-400 p-1" title={RESTRICTED_HINT}><Lock className="h-4 w-4" /></span>
                  )}
                  {visible && (
                    <button
                      onClick={() => handlePreviewDocumento(d)}
                      className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
                      title={esVigente ? 'Ver la versión vigente' : 'Ver esta versión anterior'}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        };

        return (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
            onClick={() => setHistorialDoc(null)}
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 p-5 border-b bg-gradient-to-r from-gray-50 to-white rounded-t-xl">
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <History className="h-5 w-5 text-indigo-600 shrink-0" />
                    Historial de versiones
                  </h3>
                  <div className="flex items-center flex-wrap gap-2 mt-2">
                    <span className="text-sm text-gray-600 truncate" title={historialDoc.nombre_documento}>
                      {historialDoc.nombre_documento || historialDoc.nombre_original}
                    </span>
                    {claseDoc && (
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${CLASE_EPP_BADGE[claseDoc]}`} title={CLASE_EPP_HINT[claseDoc]}>
                        {CLASE_EPP_LABEL[claseDoc]}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => setHistorialDoc(null)} className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 shrink-0" title="Cerrar">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-5 overflow-y-auto space-y-2">
                {fila(historialDoc, true)}
                {cargando ? (
                  <div className="flex justify-center py-6"><LoadingSpinner /></div>
                ) : entry.error ? (
                  <p className="text-sm text-red-500 pt-2">{entry.error}</p>
                ) : versiones.length === 0 ? (
                  <p className="text-sm text-gray-500 italic pt-2">
                    Este documento no tiene versiones anteriores: es la única que se ha subido.
                  </p>
                ) : (
                  versiones.map((h: any) => fila(h, false))
                )}
              </div>

              <div className="px-5 py-3 border-t bg-gray-50 rounded-b-xl flex justify-between items-center gap-2">
                <span className="text-xs text-gray-500">
                  {cargando || entry.error
                    ? ''
                    : `${versiones.length} versión${versiones.length === 1 ? '' : 'es'} anterior${versiones.length === 1 ? '' : 'es'}`}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExportarVersiones}
                    disabled={exportandoVersiones || cargando || !!entry.error}
                    className="bg-white hover:bg-indigo-50 text-indigo-700 border border-indigo-300 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Descargar un PDF con la versión vigente y todas las anteriores"
                  >
                    {exportandoVersiones ? <LoadingSpinner size="sm" text="" className="mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                    {exportandoVersiones ? 'Generando…' : 'Exportar PDF'}
                  </button>
                  <button onClick={() => setHistorialDoc(null)} className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de Confirmación de Eliminación con Clones */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-red-600 mb-2">⚠️ Documento con clones</h3>
              <p className="text-gray-700">
                Este documento tiene <strong>{deleteConfirmation.totalClones} clon(es)</strong> asociados. 
                Si elimina el documento original, también se eliminarán todos los clones.
              </p>
            </div>

            {deleteConfirmation.clones.length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Clones que serán eliminados:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {deleteConfirmation.clones.map((clon: any, index: number) => (
                    <li key={index} className="flex items-center">
                      <span className="mr-2">•</span>
                      <span>{clon.tipo_documento || `Clon ID: ${clon.id}`}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation({ show: false, docId: null, hasClones: false, totalClones: 0, clones: [] })}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDeleteWithClones}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Eliminar todo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Unificado de Cambio de Datos */}
      {currentPersonal && (
        <ChangePersonalDataModal
          isOpen={showChangeDataModal}
          onClose={() => setShowChangeDataModal(false)}
          currentRut={currentPersonal.rut}
          currentNombre={currentPersonal.nombre || ''}
          currentApellido={currentPersonal.apellido || ''}
          personName={`${currentPersonal.nombre || ''} ${currentPersonal.apellido || ''}`.trim() || 'Sin nombre'}
          onSuccess={(changes: { rut?: { old: string; new: string }; nombre?: { old: string; new: string; oldApellido: string; newApellido: string } }) => {
            console.log('Datos cambiados exitosamente:', changes);

            let message = 'Datos actualizados';
            if (changes.nombre && changes.rut) {
              message = `Nombre y RUT actualizados. RUT: ${changes.rut.old} → ${changes.rut.new}`;
            } else if (changes.nombre) {
              message = `Nombre actualizado: ${changes.nombre.new}`;
            } else if (changes.rut) {
              message = `RUT actualizado: ${changes.rut.old} → ${changes.rut.new}`;
            }

            toast.success(message);
            setShowChangeDataModal(false);

            // Si cambió el RUT, cerrar el modal y actualizar lista
            if (changes.rut) {
              // Cerrar modal inmediatamente porque el RUT cambió
              setTimeout(() => {
                onClose();
                onUpdate();
              }, 500);
            } else {
              // Si solo cambió el nombre, actualizar el estado local
              if (currentPersonal && changes.nombre) {
                const [newNombre, newApellido] = changes.nombre.new.split(' ', 2);
                const updatedPersonal = {
                  ...currentPersonal,
                  nombre: newNombre || '',
                  apellido: newApellido || '',
                  nombres: changes.nombre.new
                } as Personal;
                setCurrentPersonal(updatedPersonal);
              }

              // Actualizar lista
              setTimeout(() => {
                onUpdate();
              }, 500);
            }
          }}
        />
      )}

      {/* Modal para actualización masiva de centro de costo */}
      <CentroCostoMassModal
        isOpen={showCentroMassModal}
        onClose={() => setShowCentroMassModal(false)}
        centros={availableCentros}
      />

    </div>
  );
};

export default PersonalDetailModal;
